import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import i18next from "i18next";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { fetchFromApi } from "../helpers/fetchFromApi";
import { ActionConfirmationDialog } from "./ActionConfirmationDialog";
import { LeagueDisplay } from "./LeagueDisplay";
import { LeagueTablePanel } from "./LeagueTablePanel";
import { TeamStatisticsTable } from "./TeamStatisticsTable";
import { CompactLeagueTable } from "./CompactLeagueTable";
import { LeagueSeasonComparison } from "./LeagueSeasonComparison";

const ALL_LEAGUES = "all";
const ALL_SEASONS = "all";

const NO_LEAGUE = "";
const NO_SEASON = "";

function isLeagueCompetition(league) {
  return (
    String(league?.type || "").toLowerCase() === "league" &&
    String(league?.sub_type || "").toLowerCase() === "domestic"
  );
}

function formatNumber(value, digits = 3) {
  if (value === null || value === undefined || value === "") {
    return "–";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "–";
  }

  return number.toLocaleString("de-DE", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatCount(value) {
  if (value === null || value === undefined || value === "") {
    return "–";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "–";
  }

  return number.toLocaleString("de-DE", {
    maximumFractionDigits: 1,
  });
}

function formatPercent(value, digits = 1) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "–";
  }

  return `${(number * 100).toLocaleString("de-DE", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} %`;
}

function Row({ label, value }) {
  return (
    <>
      <Typography>{label}</Typography>
      <Typography>{value}</Typography>
    </>
  );
}

function emptyTransitionSummary() {
  return {
    team_count: 0,
    matches_played: 0,
    goals_for: 0,
    goals_against: 0,
  };
}

function addTransitionSummary(target, source) {
  if (!source) return;

  target.team_count += Number(source.team_count || 0);
  target.matches_played += Number(source.matches_played || 0);
  target.goals_for += Number(source.goals_for || 0);
  target.goals_against += Number(source.goals_against || 0);
}

function finishTransitionSummary(summary) {
  return {
    ...summary,

    quotient:
      summary.goals_against > 0
        ? summary.goals_for / summary.goals_against
        : null,

    team_goal_average:
      summary.matches_played > 0
        ? (summary.goals_for + summary.goals_against) / summary.matches_played
        : null,
  };
}

function TransitionSummary({ title, summary, leagueGoalAverage }) {
  if (!summary) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 0.75,
        }}
      >
        <Row label="Teams" value={summary.team_count} />

        <Row label="Spiele" value={summary.matches_played} />

        <Row label="Tore erzielt" value={summary.goals_for} />

        <Row label="Tore kassiert" value={summary.goals_against} />

        <Row label="Quotient" value={formatNumber(summary.quotient)} />

        <Row
          label={`Toreschnitt der ${title}`}
          value={formatNumber(
            summary.team_goal_average ?? summary.goal_average,
          )}
        />

        <Row
          label="Toreschnitt der Liga"
          value={formatNumber(leagueGoalAverage)}
        />
      </Box>
    </Paper>
  );
}

function LeagueReferenceSummary({ result }) {
  if (!result?.aggregate) return null;

  const actual = result.aggregate.actual;

  const model = result.aggregate.model_comparison;

  const comparisonActual = model.actual || {};

  function CompareRow({ label, expected, occurred }) {
    return (
      <>
        <Typography>{label}</Typography>
        <Typography sx={{ textAlign: "right" }}>{expected}</Typography>
        <Typography sx={{ textAlign: "right" }}>{occurred}</Typography>
      </>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        League Reference Stats
      </Typography>

      <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
        Gesamtergebnis
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 0.75,
        }}
      >
        <Row label="Saisons" value={result.aggregate.season_count} />

        <Row label="Spiele" value={actual.fixtures_played} />

        <Row label="Heimsiege" value={actual.home_wins} />

        <Row label="Remis" value={actual.draws} />

        <Row label="Auswärtssiege" value={actual.away_wins} />

        <Row label="Heimtore" value={actual.home_goals} />

        <Row label="Auswärtstore" value={actual.away_goals} />

        <Row label="Tore insgesamt" value={actual.goals} />

        <Row label="Toreschnitt" value={formatNumber(actual.goal_average)} />

        <Row label="Heimvorteil" value={formatNumber(actual.home_advantage)} />

        <Row label="Remis in Prozent" value={formatPercent(actual.draw_rate)} />
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Vergleich der berechneten Spiele
      </Typography>

      <Typography sx={{ mb: 1 }}>
        {model.fixtures_calculated} von {model.fixtures_expected} Spielen
        berechnet
        {model.complete ? " – vollständig" : " – nicht vollständig"}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(180px, 1fr) auto auto",
          columnGap: 3,
          rowGap: 0.75,
          alignItems: "baseline",
        }}
      >
        <Typography />

        <Typography
          sx={{
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          Erwartet
        </Typography>

        <Typography
          sx={{
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          Eingetroffen
        </Typography>

        <CompareRow
          label="Spiele"
          expected={model.fixtures_calculated}
          occurred={comparisonActual.fixtures_played}
        />

        <CompareRow
          label="Heimsiege"
          expected={formatCount(model.expected_home_wins)}
          occurred={comparisonActual.home_wins}
        />

        <CompareRow
          label="Remis"
          expected={formatCount(model.expected_draws)}
          occurred={comparisonActual.draws}
        />

        <CompareRow
          label="Auswärtssiege"
          expected={formatCount(model.expected_away_wins)}
          occurred={comparisonActual.away_wins}
        />

        <CompareRow
          label="Heimtore"
          expected={formatNumber(model.expected_home_goals)}
          occurred={formatNumber(comparisonActual.home_goals)}
        />

        <CompareRow
          label="Auswärtstore"
          expected={formatNumber(model.expected_away_goals)}
          occurred={formatNumber(comparisonActual.away_goals)}
        />

        <CompareRow
          label="Tore insgesamt"
          expected={formatNumber(model.expected_goals)}
          occurred={formatNumber(comparisonActual.goals)}
        />

        <CompareRow
          label="Toreschnitt"
          expected={formatNumber(model.expected_goal_average)}
          occurred={formatNumber(comparisonActual.goal_average)}
        />

        <CompareRow
          label="Heimvorteil"
          expected={formatNumber(model.expected_home_advantage)}
          occurred={formatNumber(comparisonActual.home_advantage)}
        />

        <CompareRow
          label="Remis in Prozent"
          expected={formatPercent(model.expected_draw_rate)}
          occurred={formatPercent(comparisonActual.draw_rate)}
        />

        <CompareRow
          label="Festlegung"
          expected={formatPercent(
            result.aggregate.fixation?.expected_average_probability,
          )}
          occurred={formatPercent(
            result.aggregate.fixation?.occurred_average_probability,
          )}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Vergleich mit Should Goals
      </Typography>

      {Number(
        result.aggregate.should_goals_comparison?.fixtures_compared || 0,
      ) > 0 ? (
        <>
          <Typography sx={{ mb: 1 }}>
            {result.aggregate.should_goals_comparison.fixtures_compared} Spiele
            mit vollständigen Vergleichsdaten
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(180px, 1fr) auto auto",
              columnGap: 3,
              rowGap: 0.75,
              alignItems: "baseline",
            }}
          >
            <Typography />

            <Typography
              sx={{
                fontWeight: 600,
                textAlign: "right",
              }}
            >
              Should
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                textAlign: "right",
              }}
            >
              Eingetroffen
            </Typography>

            <CompareRow
              label="Spiele"
              expected={
                result.aggregate.should_goals_comparison.fixtures_compared
              }
              occurred={
                result.aggregate.should_goals_comparison.fixtures_compared
              }
            />

            <CompareRow
              label="Heimtore"
              expected={formatNumber(
                result.aggregate.should_goals_comparison.should_home_goals,
              )}
              occurred={formatNumber(
                result.aggregate.should_goals_comparison.actual_home_goals,
              )}
            />

            <CompareRow
              label="Auswärtstore"
              expected={formatNumber(
                result.aggregate.should_goals_comparison.should_away_goals,
              )}
              occurred={formatNumber(
                result.aggregate.should_goals_comparison.actual_away_goals,
              )}
            />

            <CompareRow
              label="Tore insgesamt"
              expected={formatNumber(
                result.aggregate.should_goals_comparison.should_goals,
              )}
              occurred={formatNumber(
                result.aggregate.should_goals_comparison.actual_goals,
              )}
            />

            <CompareRow
              label="Torschnitt"
              expected={formatNumber(
                result.aggregate.should_goals_comparison.should_goal_average,
              )}
              occurred={formatNumber(
                result.aggregate.should_goals_comparison.actual_goal_average,
              )}
            />

            <CompareRow
              label="Heimvorteil"
              expected={formatNumber(
                Number(
                  result.aggregate.should_goals_comparison.should_home_goals,
                ) +
                  Number(
                    result.aggregate.should_goals_comparison.should_away_goals,
                  ) >
                  0
                  ? Number(
                      result.aggregate.should_goals_comparison
                        .should_home_goals,
                    ) /
                      ((Number(
                        result.aggregate.should_goals_comparison
                          .should_home_goals,
                      ) +
                        Number(
                          result.aggregate.should_goals_comparison
                            .should_away_goals,
                        )) /
                        2)
                  : null,
              )}
              occurred={formatNumber(
                Number(
                  result.aggregate.should_goals_comparison.actual_home_goals,
                ) +
                  Number(
                    result.aggregate.should_goals_comparison.actual_away_goals,
                  ) >
                  0
                  ? Number(
                      result.aggregate.should_goals_comparison
                        .actual_home_goals,
                    ) /
                      ((Number(
                        result.aggregate.should_goals_comparison
                          .actual_home_goals,
                      ) +
                        Number(
                          result.aggregate.should_goals_comparison
                            .actual_away_goals,
                        )) /
                        2)
                  : null,
              )}
            />
          </Box>
        </>
      ) : (
        <Alert severity="info">
          Für diese Auswahl konnten keine Should Goals berechnet werden.
        </Alert>
      )}
    </Paper>
  );
}


function SeasonBaseTable({ result }) {
  const record =
    result?.record;

  if (
    !result ||
    !result.success ||
    !result.found ||
    !record
  ) {
    return null;
  }

  const rows =
    Array.isArray(
      record.actual_table
    )
      ? record.actual_table
      : [];

  const summary =
    record.league_summary || {};

  const games =
    Number(
      summary.fixtures_played ||
      0
    );

  const teamCount =
    Number(
      summary.team_count ||
      rows.length
    );

  const tableStatus =
    record.build_status
      ?.actual_table_complete
      ? "Tabelle vollständig"
      : "Tabelle unvollständig";

  return (
    <LeagueTablePanel
      rows={rows}
      summary={summary}
      title={
        `${record.league_name} · League ${record.league_id} · ` +
        `Season ${record.season_id} · ${record.season_name}`
      }
      subtitle={
        `${teamCount} Teams · ${games} Spiele · ${tableStatus}`
      }
      showTableMode
      renderStats
      transitionItems={
        record.team_transitions?.items || []
      }
      standingRules={
        record.standing_rules?.items || []
      }
      outgoingItems={
        record.outgoing_transitions?.items || []
      }
      playoffOutcomes={
        record.playoff_outcomes || []
      }
    />
  );
}


function TeamStatisticsPanel({
  result,
  scope,
  setScope,
}) {
  const [valueMode, setValueMode] = useState("average");
  if (!result) {
    return null;
  }

  if (!result?.success) {
    return (
      <Alert severity="error">
        {result?.message ||
          "Team Statistics konnten nicht geladen werden."}
      </Alert>
    );
  }

  const rows =
    Array.isArray(result?.rows)
      ? result.rows
      : [];

  const diagnostics =
    result?.diagnostics ||
    {};

  if (!rows.length) {
    return (
      <Alert severity="info">
        Für diese Saison sind noch keine
        Team Statistics verfügbar.
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1 }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          mb: 0.25,
        }}
      >
        {i18next.t(
          "help.tableStats.teamStatistics"
        )}
      </Typography>

      <Typography
        color="text.secondary"
        sx={{
          mb: 0.5,
          fontSize: "0.78rem",
        }}
      >
        {result?.league_name ||
          `League ${result?.league_id}`}
        {" · "}
        {result?.season_name ||
          `Season ${result?.season_id}`}
        {" · "}
        {diagnostics?.team_count ?? rows.length}
        {" Teams · "}
        {diagnostics?.fixtures_used ?? 0}
        {" Fixtures verwendet"}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Button
          size="small"
          variant={
            scope === "overall"
              ? "contained"
              : "outlined"
          }
          onClick={() =>
            setScope("overall")
          }
        >
          Overall
        </Button>

        <Button
          size="small"
          variant={
            scope === "home"
              ? "contained"
              : "outlined"
          }
          onClick={() =>
            setScope("home")
          }
        >
          Home
        </Button>

        <Button
          size="small"
          variant={
            scope === "away"
              ? "contained"
              : "outlined"
          }
          onClick={() =>
            setScope("away")
          }
        >
          Away
        </Button>
      </Stack>

      <ToggleButtonGroup
        value={valueMode}
        exclusive
        size="small"
        onChange={(event, value) => value && setValueMode(value)}
        sx={{ mb: 1 }}
      >
        <ToggleButton value="average">{i18next.t("help.tableStats.average")}</ToggleButton>
        <ToggleButton value="total">{i18next.t("help.tableStats.total")}</ToggleButton>
      </ToggleButtonGroup>

      {Number(
        diagnostics?.fixtures_without_should_goals ||
        0
      ) > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
        >
          Für{" "}
          {
            diagnostics
              .fixtures_without_should_goals
          }{" "}
          Fixtures konnten keine vollständigen
          Should Goals berechnet werden.
        </Alert>
      )}

      <TeamStatisticsTable
        result={result}
        tableMode={scope}
        valueMode={valueMode}
      />
    </Paper>
  );
}

export function StatisticsDialog({
  open,
  setOpen,
  leagues = [],
  initialSection = "league_reference",
  isGold = false,
  isAdmin = false,
  onOpenOfficialStandings,
}) {
  const stopRequested = useRef(false);
  const stopAfterLeagueRequested = useRef(false);

  /*
   * Verhindert, dass verspätete Antworten einer
   * zuvor gewählten Liga/Saison die aktuelle
   * Tabellenanzeige überschreiben.
   */
  const seasonBaseRequestId = useRef(0);
  const leagueSeasonsRequestId = useRef(0);
  const seasonTeamsRequestId = useRef(0);

  const [activeSection, setActiveSection] = useState(initialSection);

  const sectionTitles = {
    official_tables: "Offizielle Tabellen",
    team_transition: "Team Transition Stats",
    table_archive: "Table Archive",
    table_stats: "Meine Statistiken / betty Tabellen",
    admin_table_stats: "Admin Table Stats",
  };

  const leagueCompetitions = useMemo(
    () => leagues.filter(isLeagueCompetition),
    [leagues],
  );

  const [leagueId, setLeagueId] = useState(ALL_LEAGUES);

  const [seasonId, setSeasonId] = useState(ALL_SEASONS);

  const [leagueSeasons, setLeagueSeasons] = useState([]);

  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [transitionResult, setTransitionResult] = useState(null);

  const [transitionSummaryResult, setTransitionSummaryResult] = useState(null);

  const [clearTransitionResult, setClearTransitionResult] = useState(null);

  const [leagueReferenceResult, setLeagueReferenceResult] = useState(null);

  const [fixtureOddsResult, setFixtureOddsResult] = useState(null);

  const [seasonDiagnosticsResult, setSeasonDiagnosticsResult] = useState(null);

  const [seasonBaseBuildResult, setSeasonBaseBuildResult] = useState(null);

  const [seasonBaseRecordResult, setSeasonBaseRecordResult] = useState(null);

  const [loadingSeasonBaseRecord, setLoadingSeasonBaseRecord] = useState(false);
  const [seasonTeams, setSeasonTeams] = useState([]);

  const [
    tableStatsView,
    setTableStatsView,
  ] = useState("actual");

  const [
    userSeasonStatsState,
    setUserSeasonStatsState,
  ] = useState(null);

  const [
    expectedDiagnosis,
    setExpectedDiagnosis,
  ] = useState(null);

  const [
    shouldDiagnosis,
    setShouldDiagnosis,
  ] = useState(null);

  const [
    loadingUserSeasonStats,
    setLoadingUserSeasonStats,
  ] = useState(false);

  const [
    teamStatisticsResult,
    setTeamStatisticsResult,
  ] = useState(null);

  const [
    loadingTeamStatistics,
    setLoadingTeamStatistics,
  ] = useState(false);

  const [
    teamStatisticsScope,
    setTeamStatisticsScope,
  ] = useState("overall");

  const [
    compilingMissingFixtures,
    setCompilingMissingFixtures,
  ] = useState(false);

  const [
    repairingTables,
    setRepairingTables,
  ] = useState(false);

  const [
    tableRepairResult,
    setTableRepairResult,
  ] = useState(null);

  const [historyPreview, setHistoryPreview] = useState(null);
  const [historyProgress, setHistoryProgress] = useState({ current: 0, total: 0 });
  const [historyRunning, setHistoryRunning] = useState(false);
  const [historyResumeCursor, setHistoryResumeCursor] = useState(null);
  const [historyOutcome, setHistoryOutcome] = useState(null);
  const [seasonDataMaintenance, setSeasonDataMaintenance] = useState(null);
  const [seasonDataRunning, setSeasonDataRunning] = useState(false);

  const [
    compileMissingProgress,
    setCompileMissingProgress,
  ] = useState({
    current: 0,
    total: 0,
  });

  const [fixtureOddsConfirmOpen, setFixtureOddsConfirmOpen] = useState(false);

  const [hideFixtureOddsConfirmation, setHideFixtureOddsConfirmation] =
    useState(
      () => localStorage.getItem("hideFixtureOddsConfirmation") === "true",
    );

  const [running, setRunning] = useState(false);

  const [actionConfirmation, setActionConfirmation] = useState(null);

  function closeActionConfirmation() {
    setActionConfirmation(null);
  }

  function confirmAction() {
    const action = actionConfirmation?.action;

    setActionConfirmation(null);

    if (typeof action === "function") {
      action();
    }
  }

  function summarizeHistoryResults(results) {
    return {
      total: results.length,
      complete: results.filter((item) => item.action === "unchanged").length,
      repairable: results.filter((item) => item.would_write || ["built", "replaced"].includes(item.action)).length,
      partial: results.filter((item) => item.expected_status === "partial" || item.should_status === "partial").length,
      reference: results.filter((item) => item.action === "reference" || item.reference_available).length,
      unavailable: results.filter((item) => ["unavailable", "irrelevant"].includes(item.action)).length,
    };
  }

  async function runHistoryRepair(endpoint, startCursor = null, previousResults = []) {
    const results = [...previousResults];
    let cursor = startCursor;
    let total = 0;

    do {
      const response = await fetchViaPost(endpoint, JSON.stringify({
        limit: 5,
        ...(cursor ? { cursor } : {}),
      }));
      const result = await response.json();
      results.push(...(result?.results || []));
      total = Number(result?.inventory?.candidates || total || results.length);
      setHistoryProgress({ current: results.length, total });
      if (!response.ok || !result?.success) {
        const error = new Error(result?.message || "Die historischen Daten konnten nicht vollständig verarbeitet werden.");
        error.nextCursor = result?.next_cursor || cursor;
        error.results = results;
        throw error;
      }
      cursor = result.next_cursor;
    } while (cursor);

    return { results, total };
  }

  async function previewHistoricalData(preserveOutcome = false) {
    setHistoryRunning(true);
    if (!preserveOutcome) setHistoryOutcome(null);
    setHistoryResumeCursor(null);
    setHistoryProgress({ current: 0, total: 0 });
    try {
      const preview = await runHistoryRepair("preview-complete-user-season-stats-repair");
      setHistoryPreview({ ...preview, summary: summarizeHistoryResults(preview.results) });
    } catch (error) {
      setHistoryOutcome({ success: false, message: error.message });
    } finally {
      setHistoryRunning(false);
    }
  }

  async function completeHistoricalData() {
    setHistoryRunning(true);
    setHistoryOutcome(null);
    setHistoryProgress({ current: 0, total: historyPreview?.total || 0 });
    try {
      const applied = await runHistoryRepair(
        "complete-user-season-stats-repair",
        historyResumeCursor,
        historyResumeCursor ? (historyOutcome?.results || []) : [],
      );
      const summary = summarizeHistoryResults(applied.results);
      setHistoryResumeCursor(null);
      setHistoryOutcome({
        success: true,
        message: `Fertig. ${summary.repairable} ${summary.repairable === 1 ? "Saison wurde" : "Saisons wurden"} ergänzt.`,
      });
      if (leagueId !== NO_LEAGUE && seasonId !== NO_SEASON) {
        await loadUserSeasonStats(leagueId, seasonId);
        await diagnoseExpectedTable(leagueId, seasonId);
      }
      await previewHistoricalData(true);
    } catch (error) {
      setHistoryResumeCursor(error.nextCursor || null);
      setHistoryOutcome({
        success: false,
        message: "Die Verarbeitung wurde an einer Saison angehalten. Bereits ergänzte Saisons bleiben erhalten.",
        results: error.results || [],
      });
    } finally {
      setHistoryRunning(false);
    }
  }

  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    leagueName: "",
    countryName: "",
    action: "",
  });

  useEffect(() => {
    if (!open) return;

    setActiveSection(initialSection);

    if (
      initialSection === "table_archive" ||
      initialSection === "table_stats" ||
      initialSection === "admin_table_stats"
    ) {
      setLeagueId(NO_LEAGUE);
      setSeasonId(NO_SEASON);

      /*
       * Table Stats startet bewusst neutral.
       *
       * Alte Loading-Zustände eines vorherigen
       * Liga-/Season-Requests dürfen beim erneuten
       * Öffnen keinen Busy-Balken verursachen.
       */
      leagueSeasonsRequestId.current += 1;
      seasonBaseRequestId.current += 1;

      setLoadingSeasons(false);
      setLoadingSeasonBaseRecord(false);

      setLeagueSeasons([]);
      setSeasonBaseRecordResult(null);
      setSeasonBaseBuildResult(null);
      setSeasonDiagnosticsResult(null);

      return;
    }

    setLeagueId((current) => {
      if (current === ALL_LEAGUES) {
        return current;
      }

      if (
        leagueCompetitions.some(
          (league) => Number(league.id) === Number(current),
        )
      ) {
        return current;
      }

      return ALL_LEAGUES;
    });
  }, [
    open,
    leagueCompetitions,
    initialSection,
    isGold,
  ]);

  async function loadLeagueReferenceStats(
    requestedLeagueId = leagueId,
    requestedSeasonId = seasonId,
  ) {
    const requestId =
      ++leagueSeasonsRequestId.current;
    if (
      !open ||
      (
        activeSection !== "league_reference" &&
        activeSection !== "table_stats" &&
        activeSection !== "admin_table_stats"
      )
    ) {
      return;
    }

    if (
      requestedLeagueId === ALL_LEAGUES ||
      requestedLeagueId === NO_LEAGUE
    ) {
      setLeagueSeasons([]);

      setSeasonId(
        activeSection === "table_stats" ||
        activeSection === "admin_table_stats"
          ? NO_SEASON
          : ALL_SEASONS
      );

      setLeagueReferenceResult(null);
      return;
    }

    setLoadingSeasons(true);

    try {
      const response = await fetchViaPost(
        "get-league-reference-stats",
        JSON.stringify({
          league_id: Number(requestedLeagueId),

          season_id:
            requestedSeasonId === ALL_SEASONS
              ? null
              : Number(requestedSeasonId),
        }),
      );

      const result = await response.json();

      if (!result?.success) {
        setLeagueReferenceResult({
          success: false,
          message:
            result?.message ||
            "League Reference Stats konnten nicht geladen werden.",
        });

        return;
      }

      /*
       * Die vorhandenen gespeicherten Saisonrecords
       * liefern nur jene Saisons, für die bereits
       * League Reference Stats existieren.
       *
       * Für das Saison-Dropdown wollen wir dagegen
       * alle aktuell bei Sportmonks verfügbaren
       * Saisons der Liga kennen - unabhängig davon,
       * ob sie schon berechnet wurden.
       */
      let sportmonksSeasons = [];

      try {
        const prepareResponse = await fetchViaPost(
          "recalculate-league-reference-stats",
          JSON.stringify({
            mode: "prepare_progress",
            league_id: Number(requestedLeagueId),
          }),
        );

        const prepared = await prepareResponse.json();

        if (prepared?.success) {
          sportmonksSeasons = (prepared.seasons || []).map((season) => ({
            id: Number(season.season_id),

            name: season.season_name || `Season ${season.season_id}`,

            starting_at: season.starting_at || null,

            ending_at: season.ending_at || null,

            fixture_total: Number(season.fixture_total || 0),
          }));
        }
      } catch (error) {
        console.warn("[League Reference Stats seasons]", error);
      }

      const storedSeasons = (result.available_seasons || []).map((season) => ({
        id: Number(season.season_id),

        name: season.season_name || `Season ${season.season_id}`,

        starting_at: season.starting_at || null,

        ending_at: season.ending_at || null,
      }));

      /*
       * Sportmonks ist die Quelle für die aktuell
       * verfügbaren Saisons.
       *
       * Bereits gespeicherte Saisonrecords werden
       * zusätzlich hineingemischt, falls ein älterer
       * Record vorhanden ist, den Sportmonks aktuell
       * nicht mehr liefert.
       */
      const seasonMap = new Map();

      for (const season of [...sportmonksSeasons, ...storedSeasons]) {
        if (Number.isFinite(Number(season.id))) {
          seasonMap.set(Number(season.id), {
            ...(seasonMap.get(Number(season.id)) || {}),
            ...season,
          });
        }
      }

      const availableSeasons = [...seasonMap.values()].sort((a, b) =>
        String(b.starting_at || "").localeCompare(String(a.starting_at || "")),
      );

      if (
        requestId !==
        leagueSeasonsRequestId.current
      ) {
        return;
      }

      setLeagueSeasons(availableSeasons);

      if (result.found) {
        setLeagueReferenceResult(result);
      } else {
        setLeagueReferenceResult(null);
      }
    } catch (error) {
      setLeagueReferenceResult({
        success: false,
        message: error?.message || String(error),
      });
    } finally {
      setLoadingSeasons(false);
    }
  }

  /*
   * League Reference Stats:
   * Hier ist die konkrete Saison weiterhin
   * Teil des eigentlichen Statistikabrufs.
   */
  useEffect(() => {
    if (
      !open ||
      activeSection !==
        "league_reference"
    ) {
      return;
    }

    loadLeagueReferenceStats(
      leagueId,
      seasonId
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    activeSection,
    leagueId,
    seasonId,
  ]);


  /*
   * USER TABLE STATS:
   *
   * Nur bereits gespeicherte Season Base
   * Records verwenden.
   *
   * KEIN prepare_progress.
   * KEINE Sportmonks-Abfrage.
   */
  useEffect(() => {
    if (
      !open ||
      (
        activeSection !==
          "table_archive" &&
        activeSection !==
          "table_stats"
      )
    ) {
      return;
    }

    loadStoredSeasonBaseSeasons(
      leagueId
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    activeSection,
    leagueId,
  ]);


  /*
   * ADMIN TABLE STATS:
   *
   * Der Admin benötigt weiterhin auch
   * Sportmonks-Saisons, für die noch kein
   * Season Base Record existiert.
   */
  useEffect(() => {
    if (
      !open ||
      activeSection !==
        "admin_table_stats"
    ) {
      return;
    }

    loadLeagueReferenceStats(
      leagueId,
      ALL_SEASONS
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    activeSection,
    leagueId,
  ]);

  const selectedLeague = leagueCompetitions.find(
    (league) => Number(league.id) === Number(leagueId),
  );

  const selectedLeagues =
    leagueId === ALL_LEAGUES
      ? leagueCompetitions
      : selectedLeague
      ? [selectedLeague]
      : [];

  async function callFunction(functionName, league) {
    const response = await fetchViaPost(
      functionName,
      JSON.stringify({
        league_id: Number(league.id),
      }),
    );

    return await response.json();
  }

  function requestStop() {
    stopRequested.current = true;
  }

  function requestStopAfterLeague() {
    stopAfterLeagueRequested.current = true;
  }

  function clearTeamTransitionStats() {
    setActionConfirmation({
      title: "Team Transition Stats löschen",
      paragraphs: [
        "Alle Datensätze aus TEAM_TRANSITION_STATS werden gelöscht.",
        "Diese Aktion betrifft die gespeicherten Transition-Rohdaten. Sie können später durch einen neuen Recalculate-Durchlauf wieder erzeugt werden.",
      ],
      confirmLabel: "Daten löschen",
      confirmColor: "error",
      action: executeClearTeamTransitionStats,
    });
  }

  async function executeClearTeamTransitionStats() {
    setRunning(true);
    setClearTransitionResult(null);
    setTransitionResult(null);
    setTransitionSummaryResult(null);

    try {
      const response = await fetchViaPost(
        "clear-team-transition-stats",
        JSON.stringify({}),
      );

      const result = await response.json();

      setClearTransitionResult(result);
    } catch (error) {
      setClearTransitionResult({
        success: false,
        message: error?.message || String(error),
      });
    } finally {
      setRunning(false);
    }
  }

  async function loadTeamTransitionSummary() {
    setRunning(true);
    setTransitionSummaryResult(null);

    try {
      const body =
        leagueId === ALL_LEAGUES
          ? {}
          : {
              league_id: Number(leagueId),
            };

      const response = await fetchViaPost(
        "get-team-transition-summary",
        JSON.stringify(body),
      );

      const result = await response.json();

      setTransitionSummaryResult(result);
    } catch (error) {
      setTransitionSummaryResult({
        success: false,
        message: error?.message || String(error),
      });
    } finally {
      setRunning(false);
    }
  }

  function recalculateTeamTransitionStats() {
    if (!selectedLeagues.length) return;

    const scope =
      leagueId === ALL_LEAGUES
        ? "alle ausgewählten Ligawettbewerbe"
        : `die Liga ${selectedLeague?.name || leagueId}`;

    setActionConfirmation({
      title: "Team Transition Stats neu berechnen",
      paragraphs: [
        `Der Durchlauf untersucht ${scope} und berechnet die Vergleichswerte für Aufsteiger und Absteiger neu.`,
        "Vorhandene Transition-Statistiken werden dabei aktualisiert beziehungsweise neu erzeugt.",
        "Der Vorgang kann je nach Auswahl einige Zeit dauern. Nach der aktuell bearbeiteten Liga kann der Durchlauf abgebrochen werden.",
      ],
      confirmLabel: "Berechnung starten",
      action: executeRecalculateTeamTransitionStats,
    });
  }

  async function executeRecalculateTeamTransitionStats() {
    setRunning(true);
    stopRequested.current = false;
    setTransitionResult(null);

    const promoted = emptyTransitionSummary();
    const relegated = emptyTransitionSummary();

    let leagueMatches = 0;
    let leagueGoals = 0;
    let recordsWritten = 0;
    let transitionsProcessed = 0;
    const errors = [];

    try {
      for (let index = 0; index < selectedLeagues.length; index += 1) {
        if (stopRequested.current) {
          break;
        }

        const league = selectedLeagues[index];

        setProgress({
          current: index + 1,
          total: selectedLeagues.length,
          leagueName: league.name,
          countryName: league.country?.name || "",
          action: "Team Transition Stats",
        });

        const result = await callFunction(
          "recalculate-team-transition-stats",
          league,
        );

        if (!result.success) {
          errors.push({
            league_id: league.id,
            league_name: league.name,
            message: result.message,
          });
          continue;
        }

        addTransitionSummary(promoted, result.promoted_summary);

        addTransitionSummary(relegated, result.relegated_summary);

        leagueMatches += Number(result.league_summary?.matches_played || 0);

        leagueGoals += Number(result.league_summary?.goals || 0);

        recordsWritten += Number(result.records_written || 0);

        transitionsProcessed += Number(
          result.season_transitions_processed || 0,
        );
      }

      setTransitionResult({
        success: errors.length < selectedLeagues.length,

        message:
          leagueId === ALL_LEAGUES
            ? `${recordsWritten} Datensätze aus ` +
              `${transitionsProcessed} Saisonübergängen ` +
              `für ${selectedLeagues.length} Ligen geschrieben.`
            : `${recordsWritten} Datensätze aus ` +
              `${transitionsProcessed} Saisonübergängen geschrieben.`,

        promoted_summary: finishTransitionSummary(promoted),

        relegated_summary: finishTransitionSummary(relegated),

        league_summary: {
          matches_played: leagueMatches,
          goals: leagueGoals,
          goal_average: leagueMatches > 0 ? leagueGoals / leagueMatches : null,
        },

        errors,
        stopped: stopRequested.current,
      });
    } catch (error) {
      setTransitionResult({
        success: false,
        message: error?.message || String(error),
      });
    } finally {
      setRunning(false);
      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",
      });
    }
  }

  function recalculateLeagueReferenceStats() {
    if (!selectedLeagues.length) return;

    const scope =
      leagueId === ALL_LEAGUES
        ? "alle ausgewählten Ligawettbewerbe"
        : `die Liga ${selectedLeague?.name || leagueId}`;

    setActionConfirmation({
      title: "League Reference Stats neu berechnen",
      paragraphs: [
        `Der Durchlauf wertet ${scope} aus.`,
        "Die tatsächlichen Ergebnisse werden ausgewertet und den vorhandenen Modellberechnungen gegenübergestellt. Die daraus entstehenden League Reference Stats werden neu geschrieben.",
        "Der Vorgang kann je nach Auswahl einige Zeit dauern. Nach der aktuell bearbeiteten Liga kann der Durchlauf abgebrochen werden.",
      ],
      confirmLabel: "Berechnung starten",
      action: executeRecalculateLeagueReferenceStats,
    });
  }

  async function executeRecalculateLeagueReferenceStats() {
    setRunning(true);
    stopRequested.current = false;
    setLeagueReferenceResult(null);

    const records = [];
    const errors = [];

    try {
      for (
        let leagueIndex = 0;
        leagueIndex < selectedLeagues.length;
        leagueIndex += 1
      ) {
        if (stopRequested.current) {
          break;
        }

        const league = selectedLeagues[leagueIndex];

        /*
         * Zuerst nur feststellen, welche Saisons
         * verarbeitet werden und wie viele
         * beendete Fixtures darin liegen.
         */
        const prepareResponse = await fetchViaPost(
          "recalculate-league-reference-stats",
          JSON.stringify({
            mode: "prepare_progress",

            league_id: Number(league.id),

            ...(leagueId !== ALL_LEAGUES && seasonId !== ALL_SEASONS
              ? {
                  season_id: Number(seasonId),
                }
              : {}),
          }),
        );

        const prepared = await prepareResponse.json();

        console.log("[League Reference Stats prepare]", {
          league_id: Number(league.id),

          league_name: league.name || null,

          season_count: prepared?.season_count ?? null,

          fixture_total: prepared?.fixture_total ?? null,

          seasons: prepared?.seasons || [],
        });

        if (!prepared?.success) {
          errors.push({
            league_id: league.id,

            league_name: league.name,

            message:
              prepared?.message ||
              "Fortschrittsdaten konnten nicht vorbereitet werden.",
          });

          continue;
        }

        const seasons = prepared.seasons || [];

        /*
         * Jede Saison einzeln.
         */
        for (
          let seasonIndex = 0;
          seasonIndex < seasons.length;
          seasonIndex += 1
        ) {
          if (stopRequested.current) {
            break;
          }

          const season = seasons[seasonIndex];

          let done = false;
          let firstStep = true;

          /*
           * progress_step verarbeitet genau
           * ein Fixture pro Request.
           */
          while (!done && !stopRequested.current) {
            const response = await fetchViaPost(
              "recalculate-league-reference-stats",
              JSON.stringify({
                mode: "progress_step",

                league_id: Number(league.id),

                season_id: Number(season.season_id),

                /*
                 * Beim ersten Schritt wird
                 * ein eventuell alter,
                 * abgebrochener Zwischenstand
                 * bewusst neu begonnen.
                 */
                reset: firstStep,
              }),
            );

            firstStep = false;

            const result = await response.json();

            if (!result?.success) {
              errors.push({
                league_id: league.id,

                league_name: league.name,

                season_id: season.season_id,

                season_name: season.season_name,

                message:
                  result?.message || "Fixture konnte nicht verarbeitet werden.",
              });

              break;
            }

            done = Boolean(result.done);

            const current = Number(result.current || 0);

            const total = Number(result.total || 0);

            setProgress({
              current: leagueIndex + 1,

              total: selectedLeagues.length,

              leagueName: league.name,

              countryName: league.country?.name || "",

              action: "League Reference Stats",

              seasonCurrent: seasonIndex + 1,

              seasonTotal: seasons.length,

              seasonName: season.season_name || `Season ${season.season_id}`,

              fixtureCurrent: current,

              fixtureTotal: total,
            });

            /*
             * Nach dem letzten Fixture liefert
             * das Backend den fertigen
             * Saisonrecord zurück.
             */
            if (done && result.record) {
              records.push(result.record);
            }
          }
        }
      }

      /*
       * Nach Abschluss des Recalculate-Laufs nicht
       * mit den während des Progress-Laufs gesammelten
       * Rückgaben weiterrechnen.
       *
       * Stattdessen die tatsächlich dauerhaft
       * gespeicherten finalen Saisonrecords erneut
       * über get-league-reference-stats laden.
       *
       * Dadurch ist die Abschlussanzeige identisch
       * mit dem späteren "Anzeigen"-Weg und basiert
       * ausschließlich auf den Records, die wirklich
       * in LEAGUE_REFERENCE_STATS gespeichert wurden.
       */
      const storedRecordsByKey = new Map();

      for (const league of selectedLeagues) {
        try {
          const storedResponse = await fetchViaPost(
            "get-league-reference-stats",
            JSON.stringify({
              league_id: Number(league.id),

              season_id: seasonId === ALL_SEASONS ? null : Number(seasonId),
            }),
          );

          const storedResult = await storedResponse.json();

          if (!storedResult?.success) {
            errors.push({
              league_id: Number(league.id),

              league_name: league.name || null,

              stage: "reload_final_records",

              message:
                storedResult?.message ||
                "Gespeicherte League Reference Stats konnten nicht geladen werden.",
            });

            continue;
          }

          for (const record of storedResult.records || []) {
            const recordLeagueId = Number(record?.league_id ?? league.id);

            const recordSeasonId = Number(record?.season_id);

            if (
              !Number.isFinite(recordLeagueId) ||
              !Number.isFinite(recordSeasonId)
            ) {
              errors.push({
                league_id: Number(league.id),

                league_name: league.name || null,

                stage: "reload_final_records",

                message:
                  "Finaler Saisonrecord ohne gültige League-/Season-ID gefunden.",
              });

              continue;
            }

            const recordKey = `${recordLeagueId}:${recordSeasonId}`;

            storedRecordsByKey.set(recordKey, record);
          }
        } catch (error) {
          errors.push({
            league_id: Number(league.id),

            league_name: league.name || null,

            stage: "reload_final_records",

            message: error?.message || String(error),
          });
        }
      }

      /*
       * Die vorhandene Aggregationslogik darunter
       * darf weiterverwendet werden.
       *
       * Ihre Datenquelle sind jetzt aber ausschließlich
       * die erneut geladenen finalen Saisonrecords.
       */
      records.length = 0;

      records.push(...storedRecordsByKey.values());

      console.log(
        "[League Reference Stats]",
        `${records.length} finale Saisonrecords nach Recalculate neu geladen.`,
      );

      /*
       * Aus den final gespeicherten Saisonrecords
       * die Anzeige-Aggregation erzeugen.
       */
      let seasonCount = 0;

      let fixturesPlayed = 0;
      let homeWins = 0;
      let draws = 0;
      let awayWins = 0;
      let homeGoals = 0;
      let awayGoals = 0;

      let fixturesCalculated = 0;
      let expectedHomeWins = 0;
      let expectedDraws = 0;
      let expectedAwayWins = 0;
      let expectedHomeGoals = 0;
      let expectedAwayGoals = 0;

      let calculatedActualFixtures = 0;
      let calculatedActualHomeWins = 0;
      let calculatedActualDraws = 0;
      let calculatedActualAwayWins = 0;
      let calculatedActualHomeGoals = 0;
      let calculatedActualAwayGoals = 0;

      let fixationFixtures = 0;
      let fixationExpectedSum = 0;
      let fixationOccurredSum = 0;

      let shouldFixtures = 0;

      let shouldExpectedHomeGoals = 0;
      let shouldExpectedAwayGoals = 0;

      let shouldActualHomeGoals = 0;
      let shouldActualAwayGoals = 0;

      let shouldHomeGoals = 0;
      let shouldAwayGoals = 0;

      let shouldFixturesAttempted = 0;
      let shouldNoStatistics = 0;
      let shouldCalculationErrors = 0;
      let shouldInvalid = 0;

      const shouldExamples = [];

      for (const record of records) {
        seasonCount += 1;

        const actual = record.actual || {};

        fixturesPlayed += Number(actual.fixtures_played || 0);

        homeWins += Number(actual.home_wins || 0);

        draws += Number(actual.draws || 0);

        awayWins += Number(actual.away_wins || 0);

        homeGoals += Number(actual.home_goals || 0);

        awayGoals += Number(actual.away_goals || 0);

        const model = record.model_comparison || {};

        fixturesCalculated += Number(model.fixtures_calculated || 0);

        expectedHomeWins += Number(model.expected_home_wins || 0);

        expectedDraws += Number(model.expected_draws || 0);

        expectedAwayWins += Number(model.expected_away_wins || 0);

        expectedHomeGoals += Number(model.expected_home_goals || 0);

        expectedAwayGoals += Number(model.expected_away_goals || 0);

        const modelActual = model.actual || {};

        calculatedActualFixtures += Number(modelActual.fixtures_played || 0);

        calculatedActualHomeWins += Number(modelActual.home_wins || 0);

        calculatedActualDraws += Number(modelActual.draws || 0);

        calculatedActualAwayWins += Number(modelActual.away_wins || 0);

        calculatedActualHomeGoals += Number(modelActual.home_goals || 0);

        calculatedActualAwayGoals += Number(modelActual.away_goals || 0);

        const fixation = record.fixation || {};

        const fixationCount = Number(fixation.fixtures_compared || 0);

        fixationFixtures += fixationCount;

        fixationExpectedSum +=
          Number(fixation.expected_average_probability || 0) * fixationCount;

        fixationOccurredSum +=
          Number(fixation.occurred_average_probability || 0) * fixationCount;

        const should = record.should_goals_comparison || {};

        shouldFixtures += Number(should.fixtures_compared || 0);

        shouldExpectedHomeGoals += Number(should.expected_home_goals || 0);

        shouldExpectedAwayGoals += Number(should.expected_away_goals || 0);

        shouldActualHomeGoals += Number(should.actual_home_goals || 0);

        shouldActualAwayGoals += Number(should.actual_away_goals || 0);

        shouldHomeGoals += Number(should.should_home_goals || 0);

        shouldAwayGoals += Number(should.should_away_goals || 0);

        const diagnostics = should.diagnostics || {};

        shouldFixturesAttempted += Number(diagnostics.fixtures_attempted || 0);

        shouldNoStatistics += Number(diagnostics.no_statistics || 0);

        shouldCalculationErrors += Number(diagnostics.calculation_errors || 0);

        shouldInvalid += Number(diagnostics.invalid_should_goals || 0);

        for (const example of diagnostics.examples || []) {
          if (shouldExamples.length < 5) {
            shouldExamples.push(example);
          }
        }
      }

      const actualGoals = homeGoals + awayGoals;

      const expectedGoals = expectedHomeGoals + expectedAwayGoals;

      const calculatedActualGoals =
        calculatedActualHomeGoals + calculatedActualAwayGoals;

      const shouldExpectedGoals =
        shouldExpectedHomeGoals + shouldExpectedAwayGoals;

      const shouldActualGoals = shouldActualHomeGoals + shouldActualAwayGoals;

      const shouldGoals = shouldHomeGoals + shouldAwayGoals;

      const aggregateResult = {
        season_count: seasonCount,

        actual: {
          fixtures_played: fixturesPlayed,

          home_wins: homeWins,

          draws,

          away_wins: awayWins,

          home_goals: homeGoals,

          away_goals: awayGoals,

          goals: actualGoals,

          goal_average:
            fixturesPlayed > 0 ? actualGoals / fixturesPlayed : null,

          home_advantage:
            actualGoals > 0 ? homeGoals / (actualGoals / 2) : null,

          draw_rate: fixturesPlayed > 0 ? draws / fixturesPlayed : null,
        },

        model_comparison: {
          fixtures_calculated: fixturesCalculated,

          fixtures_expected: fixturesPlayed,

          complete: fixturesPlayed > 0 && fixturesCalculated === fixturesPlayed,

          expected_home_wins: expectedHomeWins,

          expected_draws: expectedDraws,

          expected_away_wins: expectedAwayWins,

          expected_home_goals: expectedHomeGoals,

          expected_away_goals: expectedAwayGoals,

          expected_goals: expectedGoals,

          expected_goal_average:
            fixturesCalculated > 0 ? expectedGoals / fixturesCalculated : null,

          expected_home_advantage:
            expectedGoals > 0 ? expectedHomeGoals / (expectedGoals / 2) : null,

          expected_draw_rate:
            fixturesCalculated > 0 ? expectedDraws / fixturesCalculated : null,

          actual: {
            fixtures_played: calculatedActualFixtures,

            home_wins: calculatedActualHomeWins,

            draws: calculatedActualDraws,

            away_wins: calculatedActualAwayWins,

            home_goals: calculatedActualHomeGoals,

            away_goals: calculatedActualAwayGoals,

            goals: calculatedActualGoals,

            goal_average:
              calculatedActualFixtures > 0
                ? calculatedActualGoals / calculatedActualFixtures
                : null,

            home_advantage:
              calculatedActualGoals > 0
                ? calculatedActualHomeGoals / (calculatedActualGoals / 2)
                : null,

            draw_rate:
              calculatedActualFixtures > 0
                ? calculatedActualDraws / calculatedActualFixtures
                : null,
          },
        },

        fixation: {
          fixtures_compared: fixationFixtures,

          expected_average_probability:
            fixationFixtures > 0
              ? fixationExpectedSum / fixationFixtures
              : null,

          occurred_average_probability:
            fixationFixtures > 0
              ? fixationOccurredSum / fixationFixtures
              : null,
        },

        should_goals_comparison: {
          fixtures_compared: shouldFixtures,

          expected_home_goals: shouldExpectedHomeGoals,

          expected_away_goals: shouldExpectedAwayGoals,

          expected_goals: shouldExpectedGoals,

          actual_home_goals: shouldActualHomeGoals,

          actual_away_goals: shouldActualAwayGoals,

          actual_goals: shouldActualGoals,

          should_home_goals: shouldHomeGoals,

          should_away_goals: shouldAwayGoals,

          should_goals: shouldGoals,

          expected_goal_average:
            shouldFixtures > 0 ? shouldExpectedGoals / shouldFixtures : null,

          actual_goal_average:
            shouldFixtures > 0 ? shouldActualGoals / shouldFixtures : null,

          should_goal_average:
            shouldFixtures > 0 ? shouldGoals / shouldFixtures : null,

          diagnostics: {
            fixtures_attempted: shouldFixturesAttempted,

            no_statistics: shouldNoStatistics,

            calculation_errors: shouldCalculationErrors,

            invalid_should_goals: shouldInvalid,

            examples: shouldExamples,
          },
        },
      };

      setLeagueReferenceResult({
        success: errors.length < selectedLeagues.length,

        message:
          `${seasonCount} Saisons und ` +
          `${fixturesPlayed} beendete Spiele ausgewertet.`,

        aggregate: aggregateResult,

        errors,

        stopped: stopRequested.current,
      });
    } catch (error) {
      setLeagueReferenceResult({
        success: false,

        message: error?.message || String(error),
      });
    } finally {
      /*
       * Bei einer einzelnen Liga anschließend
       * den tatsächlich gespeicherten Record
       * wieder aus KV laden.
       */
      if (leagueId !== ALL_LEAGUES && !stopRequested.current) {
        await loadLeagueReferenceStats(leagueId, seasonId);
      }

      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",

        seasonCurrent: 0,
        seasonTotal: 0,
        seasonName: "",

        fixtureCurrent: 0,
        fixtureTotal: 0,
      });
    }
  }

  async function runSeasonBaseDiagnostics() {
    if (!selectedLeagues.length) {
      return;
    }

    setRunning(true);
    stopRequested.current = false;
    setSeasonDiagnosticsResult(null);

    const leagueResults = [];
    const errors = [];

    try {
      for (let index = 0; index < selectedLeagues.length; index += 1) {
        if (stopRequested.current) {
          break;
        }

        const league = selectedLeagues[index];

        setProgress({
          current: index + 1,

          total: selectedLeagues.length,

          leagueName: league.name,

          countryName: league.country?.name || "",

          action: "Season Base Diagnostics",
        });

        try {
          const response = await fetchViaPost(
            "diagnose-season-stats",
            JSON.stringify({
              league_id: Number(league.id),

              ...(leagueId !== ALL_LEAGUES && seasonId !== ALL_SEASONS
                ? {
                    season_id: Number(seasonId),
                  }
                : {}),
            }),
          );

          const result = await response.json();

          if (!result?.success) {
            errors.push({
              league_id: Number(league.id),

              league_name: league.name,

              message: result?.message || "Diagnose fehlgeschlagen.",
            });

            continue;
          }

          leagueResults.push({
            league_id: Number(league.id),

            league_name: league.name,

            country_name: league.country?.name || "",

            ...result,
          });
        } catch (error) {
          errors.push({
            league_id: Number(league.id),

            league_name: league.name,

            message: error?.message || String(error),
          });
        }
      }

      setSeasonDiagnosticsResult({
        success: leagueResults.length > 0,

        leagues: leagueResults,

        errors,

        stopped: stopRequested.current,

        summary: {
          leagues_requested: selectedLeagues.length,

          leagues_diagnosed: leagueResults.length,

          leagues_failed: errors.length,

          seasons_diagnosed: leagueResults.reduce(
            (sum, league) =>
              sum + Number(league.summary?.seasons_diagnosed || 0),
            0,
          ),

          fixtures_total: leagueResults.reduce(
            (sum, league) => sum + Number(league.summary?.fixtures_total || 0),
            0,
          ),

          finished_with_score: leagueResults.reduce(
            (sum, league) =>
              sum + Number(league.summary?.finished_with_score || 0),
            0,
          ),

          regular_candidate_fixtures: leagueResults.reduce(
            (sum, league) =>
              sum + Number(league.summary?.regular_candidate_fixtures || 0),
            0,
          ),
        },
      });
    } catch (error) {
      setSeasonDiagnosticsResult({
        success: false,

        message: error?.message || String(error),
      });
    } finally {
      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",
      });
    }
  }

  async function loadStoredSeasonBaseSeasons(
    requestedLeagueId
  ) {
    const requestId =
      ++leagueSeasonsRequestId.current;

    if (
      !open ||
      (
        activeSection !==
          "table_archive" &&
        activeSection !==
          "table_stats"
      )
    ) {
      return;
    }

    if (
      requestedLeagueId ===
        ALL_LEAGUES ||
      requestedLeagueId ===
        NO_LEAGUE
    ) {
      setLeagueSeasons([]);
      setSeasonId(NO_SEASON);
      return;
    }

    setLoadingSeasons(true);

    try {
      const response =
        await fetchViaPost(
          "get-season-base-stats",
          JSON.stringify({
            mode:
              "list_seasons",

            league_id:
              Number(
                requestedLeagueId
              ),
          }),
        );

      const result =
        await response.json();

      if (
        requestId !==
        leagueSeasonsRequestId.current
      ) {
        return;
      }

      if (!result?.success) {
        setLeagueSeasons([]);
        return;
      }

      const seasons =
        (result.seasons || [])
          .map(
            (season) => ({
              id:
                Number(
                  season.season_id
                ),

              name:
                season.season_name ||
                `Season ${season.season_id}`,

              starting_at:
                season.starting_at ||
                null,

              ending_at:
                season.ending_at ||
                null,
            })
          )
          .filter(
            (season) =>
              Number.isFinite(
                season.id
              )
          );

      setLeagueSeasons(
        seasons
      );

      /*
       * Der User soll bewusst eine Saison
       * auswählen. Keine alte Auswahl übernehmen.
       */
      setSeasonId(
        NO_SEASON
      );
    } catch (error) {
      console.warn(
        "[Table Stats stored seasons]",
        error
      );

      setLeagueSeasons([]);
    } finally {
      if (
        requestId ===
        leagueSeasonsRequestId.current
      ) {
        setLoadingSeasons(false);
      }
    }
  }


  async function loadSelectedSeasonBaseStats(
    requestedLeagueId = leagueId,
    requestedSeasonId = seasonId,
  ) {
    const requestId =
      ++seasonBaseRequestId.current;
    if (
      (
        activeSection !== "table_archive" &&
        activeSection !== "table_stats" &&
        activeSection !== "admin_table_stats"
      ) ||
      requestedLeagueId === ALL_LEAGUES ||
      requestedLeagueId === NO_LEAGUE ||
      requestedSeasonId === ALL_SEASONS ||
      requestedSeasonId === NO_SEASON
    ) {
      setSeasonBaseRecordResult(null);
      setLoadingSeasonBaseRecord(false);
      return;
    }

    setLoadingSeasonBaseRecord(true);

    try {
      const response =
        await fetchViaPost(
          "get-season-base-stats",
          JSON.stringify({
            league_id:
              Number(
                requestedLeagueId
              ),

            season_id:
              Number(
                requestedSeasonId
              ),

            fast_read:
              activeSection ===
                "table_archive" ||
              activeSection ===
                "table_stats",
          }),
        );

      const result =
        await response.json();

      /*
       * Während dieses Requests kann der User
       * bereits Liga oder Saison gewechselt haben.
       * Eine alte Antwort darf dann nicht mehr
       * angezeigt werden.
       */
      if (
        requestId !==
        seasonBaseRequestId.current
      ) {
        return;
      }

      setSeasonBaseRecordResult(
        result
      );
    } catch (error) {
      setSeasonBaseRecordResult({
        success: false,
        found: false,
        message:
          error?.message ||
          String(error),
      });
    } finally {
      if (
        requestId ===
        seasonBaseRequestId.current
      ) {
        setLoadingSeasonBaseRecord(false);
      }
    }
  }


  async function loadUserSeasonStats(
    requestedLeagueId = leagueId,
    requestedSeasonId = seasonId
  ) {
    setSeasonDataMaintenance(null);
    if (
      activeSection !== "table_stats" ||
      requestedLeagueId === NO_LEAGUE ||
      requestedLeagueId === ALL_LEAGUES ||
      requestedSeasonId === NO_SEASON ||
      requestedSeasonId === ALL_SEASONS
    ) {
      setUserSeasonStatsState(null);
      setExpectedDiagnosis(null);
      setShouldDiagnosis(null);
      return;
    }

    setLoadingUserSeasonStats(true);

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode: "load",

            league_id:
              Number(
                requestedLeagueId
              ),

            season_id:
              Number(
                requestedSeasonId
              ),
          })
        );

      const result =
        await response.json();

      setUserSeasonStatsState(
        result
      );

    } catch (error) {
      setUserSeasonStatsState({
        success: false,
        message:
          error?.message ||
          String(error),
      });
    } finally {
      setLoadingUserSeasonStats(false);
    }
  }

  async function maintainSeasonData(mode = "diagnose_season_data") {
    setSeasonDataRunning(true);
    try {
      const response = await fetchViaPost(
        "build-user-season-stats",
        JSON.stringify({
          mode,
          league_id: Number(leagueId),
          season_id: Number(seasonId),
        }),
      );
      const result = await response.json();
      setSeasonDataMaintenance(result);
      if (response.ok && result?.success && mode !== "diagnose_season_data") {
        await loadUserSeasonStats(leagueId, seasonId);
        setSeasonDataMaintenance(result);
      }
    } catch (error) {
      setSeasonDataMaintenance({ success: false, message: error?.message || String(error) });
    } finally {
      setSeasonDataRunning(false);
    }
  }

  async function loadSeasonTeams(requestedSeasonId = seasonId) {
    const requestId = ++seasonTeamsRequestId.current;
    if (!Number.isFinite(Number(requestedSeasonId))) {
      setSeasonTeams([]);
      return;
    }
    try {
      const response = await fetchFromApi(`fetch-data-v3/teams/seasons/${Number(requestedSeasonId)}`);
      const result = await response.json();
      if (requestId === seasonTeamsRequestId.current) {
        setSeasonTeams(Array.isArray(result?.data) ? result.data : []);
      }
    } catch {
      if (requestId === seasonTeamsRequestId.current) setSeasonTeams([]);
    }
  }


  async function loadTeamStatistics(
    requestedLeagueId = leagueId,
    requestedSeasonId = seasonId
  ) {
    if (
      activeSection !== "table_stats" ||
      requestedLeagueId === NO_LEAGUE ||
      requestedLeagueId === ALL_LEAGUES ||
      requestedSeasonId === NO_SEASON ||
      requestedSeasonId === ALL_SEASONS
    ) {
      setTeamStatisticsResult(null);
      return;
    }

    setLoadingTeamStatistics(true);

    try {
      const response =
        await fetchViaPost(
          "get-team-season-statistics",
          JSON.stringify({
            league_id:
              Number(
                requestedLeagueId
              ),

            season_id:
              Number(
                requestedSeasonId
              ),
          })
        );

      const result =
        await response.json();

      setTeamStatisticsResult(
        result
      );

    } catch (error) {
      setTeamStatisticsResult({
        success: false,

        message:
          error?.message ||
          String(error),
      });

    } finally {
      setLoadingTeamStatistics(false);
    }
  }


  async function diagnoseExpectedTable(
    requestedLeagueId = leagueId,
    requestedSeasonId = seasonId
  ) {
    if (
      requestedLeagueId === NO_LEAGUE ||
      requestedLeagueId === ALL_LEAGUES ||
      requestedSeasonId === NO_SEASON ||
      requestedSeasonId === ALL_SEASONS
    ) {
      return;
    }

    setLoadingUserSeasonStats(true);
    setExpectedDiagnosis(null);
    setShouldDiagnosis(null);

    try {
      /*
       * CHECK DATA prüft beide unabhängigen
       * Table-Stats-Datenwege parallel:
       *
       * Expected:
       *   persönliche Fixture-Modellwerte
       *
       * Should:
       *   gespeicherte Match Statistics
       */
      const [
        expectedResponse,
        shouldResponse,
      ] =
        await Promise.all([
          fetchViaPost(
            "build-user-season-stats",
            JSON.stringify({
              mode:
                "diagnose",

              league_id:
                Number(
                  requestedLeagueId
                ),

              season_id:
                Number(
                  requestedSeasonId
                ),
            })
          ),

          fetchViaPost(
            "build-user-season-stats",
            JSON.stringify({
              mode:
                "diagnose_should",

              league_id:
                Number(
                  requestedLeagueId
                ),

              season_id:
                Number(
                  requestedSeasonId
                ),
            })
          ),
        ]);

      const [
        expectedResult,
        shouldResult,
      ] =
        await Promise.all([
          expectedResponse.json(),
          shouldResponse.json(),
        ]);

      setExpectedDiagnosis(
        expectedResult
      );

      setShouldDiagnosis(
        shouldResult
      );

    } catch (error) {
      const failure = {
        success: false,

        message:
          error?.message ||
          String(error),
      };

      setExpectedDiagnosis(
        failure
      );

      setShouldDiagnosis(
        failure
      );

    } finally {
      setLoadingUserSeasonStats(false);
    }
  }


  async function calculateExpectedTable() {
    if (
      leagueId === NO_LEAGUE ||
      seasonId === NO_SEASON
    ) {
      return;
    }

    setLoadingUserSeasonStats(true);

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            league_id:
              Number(leagueId),

            season_id:
              Number(seasonId),
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setUserSeasonStatsState(
          result
        );
        return;
      }

      setUserSeasonStatsState({
        success: true,
        possible: true,
        record_exists: true,
        expected_table_exists: true,
        record:
          result.record,
        message:
          result.message,
      });

      setTableStatsView(
        "expected"
      );

      await diagnoseExpectedTable();

    } catch (error) {
      setUserSeasonStatsState({
        success: false,
        message:
          error?.message ||
          String(error),
      });
    } finally {
      setLoadingUserSeasonStats(false);
    }
  }


  async function calculateShouldTable() {
    if (
      leagueId === NO_LEAGUE ||
      seasonId === NO_SEASON
    ) {
      return;
    }

    setLoadingUserSeasonStats(true);

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "build_should",

            league_id:
              Number(
                leagueId
              ),

            season_id:
              Number(
                seasonId
              ),
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setShouldDiagnosis(
          result
        );

        return;
      }

      /*
       * Nach erfolgreichem Build den kompletten
       * USER_SEASON_STATS-Record neu laden.
       *
       * Dadurch bleiben Expected und Should
       * garantiert synchron mit dem Backend.
       */
      const loadResponse =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "load",

            league_id:
              Number(
                leagueId
              ),

            season_id:
              Number(
                seasonId
              ),
          })
        );

      const loaded =
        await loadResponse.json();

      setUserSeasonStatsState(
        loaded
      );

      setTableStatsView(
        "should"
      );


      /*
       * Danach den realen Should-Zustand
       * erneut diagnostizieren.
       */
      const diagnosisResponse =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "diagnose_should",

            league_id:
              Number(
                leagueId
              ),

            season_id:
              Number(
                seasonId
              ),
          })
        );

      const diagnosis =
        await diagnosisResponse.json();

      setShouldDiagnosis(
        diagnosis
      );

    } catch (error) {
      setShouldDiagnosis({
        success: false,

        message:
          error?.message ||
          String(error),
      });

    } finally {
      setLoadingUserSeasonStats(false);
    }
  }


  async function repairUserTables() {
    if (
      leagueId === NO_LEAGUE ||
      seasonId === NO_SEASON
    ) {
      return;
    }

    setRepairingTables(true);
    setTableRepairResult(null);

    try {
      const expectedResponse =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            league_id:
              Number(leagueId),

            season_id:
              Number(seasonId),
          })
        );

      const expectedResult =
        await expectedResponse.json();

      if (
        !expectedResponse.ok ||
        !expectedResult?.success
      ) {
        throw new Error(
          expectedResult?.message ||
          i18next.t(
            "help.tableStats.repairFailed"
          )
        );
      }

      const shouldResponse =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "build_should",

            league_id:
              Number(leagueId),

            season_id:
              Number(seasonId),
          })
        );

      const shouldResult =
        await shouldResponse.json();

      if (
        !shouldResponse.ok ||
        !shouldResult?.success
      ) {
        throw new Error(
          shouldResult?.message ||
          i18next.t(
            "help.tableStats.repairFailed"
          )
        );
      }

      await loadUserSeasonStats(
        leagueId,
        seasonId
      );

      await diagnoseExpectedTable(
        leagueId,
        seasonId
      );

      setTableRepairResult({
        success: true,

        message:
          i18next.t(
            "help.tableStats.repairSuccess"
          ),
      });
    } catch (error) {
      setTableRepairResult({
        success: false,

        message:
          error?.message ||
          i18next.t(
            "help.tableStats.repairFailed"
          ),
      });
    } finally {
      setRepairingTables(false);
    }
  }


  async function compileMissingExpectedFixtures() {
    let diagnosis =
      expectedDiagnosis;

    /*
     * Falls der User direkt über diesen Weg kommt,
     * zunächst einmal sauber diagnostizieren.
     */
    if (!diagnosis?.success) {
      setLoadingUserSeasonStats(true);

      try {
        const diagnosisResponse =
          await fetchViaPost(
            "build-user-season-stats",
            JSON.stringify({
              mode: "diagnose",

              league_id:
                Number(leagueId),

              season_id:
                Number(seasonId),
            })
          );

        diagnosis =
          await diagnosisResponse.json();

        setExpectedDiagnosis(
          diagnosis
        );
      } finally {
        setLoadingUserSeasonStats(false);
      }
    }

    if (
      !diagnosis?.success ||
      !diagnosis?.possible
    ) {
      return;
    }

    const fixtureIds =
      [
        ...new Set([
          ...(
            diagnosis
              ?.missing_fixture_ids ||
            []
          ),

          ...(
            diagnosis
              ?.invalid_fixture_ids ||
            []
          ),
        ]),
      ]
        .map(Number)
        .filter(Number.isFinite);

    if (!fixtureIds.length) {
      return;
    }

    setCompilingMissingFixtures(true);
    stopRequested.current = false;

    setCompileMissingProgress({
      current: 0,
      total:
        fixtureIds.length,
    });

    const errors = [];

    try {
      for (
        let index = 0;
        index < fixtureIds.length;
        index += 1
      ) {
        if (
          stopRequested.current
        ) {
          break;
        }

        const fixtureId =
          fixtureIds[index];

        setCompileMissingProgress({
          current:
            index + 1,

          total:
            fixtureIds.length,
        });

        try {
          /*
           * WICHTIG:
           *
           * Nur genau dieses fehlende Fixture
           * des angemeldeten Users wird ergänzt.
           *
           * Kein Liga-Gesamtlauf.
           * Keine globalen Records.
           */
          const response =
            await fetchViaPost(
              "enrich-fixture-odds",
              JSON.stringify({
                fixture_id:
                  fixtureId,
              })
            );

          const result =
            await response.json();

          if (!result?.success) {
            errors.push({
              fixture_id:
                fixtureId,

              message:
                result?.message ||
                "Fixture could not be compiled.",
            });
          }
        } catch (error) {
          errors.push({
            fixture_id:
              fixtureId,

            message:
              error?.message ||
              String(error),
          });
        }
      }

      /*
       * Danach den realen Zustand erneut prüfen.
       */
      const diagnosisResponse =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode: "diagnose",

            league_id:
              Number(leagueId),

            season_id:
              Number(seasonId),
          })
        );

      const updatedDiagnosis =
        await diagnosisResponse.json();

      setExpectedDiagnosis({
        ...updatedDiagnosis,

        compile_errors:
          errors,

        compile_stopped:
          stopRequested.current,
      });

    } finally {
      setCompilingMissingFixtures(false);

      setCompileMissingProgress({
        current: 0,
        total: 0,
      });
    }
  }


  async function enrichExistingSeasonBaseStats() {
    if (!selectedLeagues.length) {
      return;
    }

    setRunning(true);
    stopRequested.current = false;

    setSeasonBaseBuildResult(null);

    let recordsFound = 0;
    let recordsWritten = 0;
    let playoffOutcomesWritten = 0;

    const errors = [];

    try {
      for (
        let index = 0;
        index < selectedLeagues.length;
        index += 1
      ) {
        if (stopRequested.current) {
          break;
        }

        const league =
          selectedLeagues[index];

        setProgress({
          current:
            index + 1,

          total:
            selectedLeagues.length,

          leagueName:
            league?.name ||
            `League ${league.id}`,

          countryName:
            league?.country?.name ||
            "",

          action:
            "Bestehende Season Base Records anreichern",

          seasonCurrent: 0,
          seasonTotal: 0,
          seasonName: "",

          fixtureCurrent: 0,
          fixtureTotal: 0,
        });

        try {
          const response =
            await fetchViaPost(
              "build-season-base-stats",
              JSON.stringify({
                mode:
                  "enrich_existing",

                league_id:
                  Number(
                    league.id
                  ),
              }),
            );

          const result =
            await response.json();

          recordsFound +=
            Number(
              result?.records_found ||
              0
            );

          recordsWritten +=
            Number(
              result?.records_written ||
              0
            );

          playoffOutcomesWritten +=
            Number(
              result?.playoff_outcomes_written ||
              0
            );

          for (
            const error of
            result?.errors || []
          ) {
            errors.push({
              league_id:
                Number(
                  league.id
                ),

              league_name:
                league.name ||
                null,

              ...error,
            });
          }

          if (
            !result?.success &&
            !result?.partial_success
          ) {
            errors.push({
              league_id:
                Number(
                  league.id
                ),

              league_name:
                league.name ||
                null,

              message:
                result?.message ||
                "Anreicherung fehlgeschlagen.",
            });
          }
        } catch (error) {
          errors.push({
            league_id:
              Number(
                league.id
              ),

            league_name:
              league.name ||
              null,

            message:
              error?.message ||
              String(error),
          });
        }
      }

      setSeasonBaseBuildResult({
        success:
          errors.length === 0,

        partial_success:
          recordsWritten > 0 &&
          errors.length > 0,

        message:
          `${recordsWritten} von ${recordsFound} vorhandenen Records angereichert. ` +
          `${playoffOutcomesWritten} Play-off-Ausgänge gespeichert. ` +
          `${errors.length} Fehler.`,

        mode:
          "enrich_existing",

        records_requested:
          recordsFound,

        records_processed:
          recordsWritten,

        records_successful:
          recordsWritten,

        records_failed:
          errors.length,

        errors,

        stopped:
          stopRequested.current,
      });

    } finally {
      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",

        seasonCurrent: 0,
        seasonTotal: 0,
        seasonName: "",

        fixtureCurrent: 0,
        fixtureTotal: 0,
      });
    }
  }


  async function getBuildSeasonsForLeague(
    league
  ) {
    const response =
      await fetchViaPost(
        "recalculate-league-reference-stats",
        JSON.stringify({
          mode: "prepare_progress",
          league_id:
            Number(league.id),
        }),
      );

    const result =
      await response.json();

    if (!result?.success) {
      throw new Error(
        result?.message ||
        `Saisons für ${league.name || league.id} konnten nicht geladen werden.`
      );
    }

    return (result.seasons || [])
      .map((season) => ({
        id:
          Number(
            season.season_id
          ),

        name:
          season.season_name ||
          `Season ${season.season_id}`,

        starting_at:
          season.starting_at ||
          null,

        ending_at:
          season.ending_at ||
          null,
      }))
      .filter(
        (season) =>
          Number.isFinite(
            Number(season.id)
          )
      );
  }


  async function getCurrentBuildSeasonForLeague(
    league
  ) {
    const response =
      await fetchViaPost(
        "recalculate-league-reference-stats",
        JSON.stringify({
          mode:
            "list_seasons",

          league_id:
            Number(
              league.id
            ),
        }),
      );

    const result =
      await response.json();

    if (!result?.success) {
      throw new Error(
        result?.message ||
        `Saisons für ${league.name || league.id} konnten nicht geladen werden.`
      );
    }

    const currentSeason =
      (result.seasons || [])
        .find(
          (season) =>
            Boolean(
              season?.is_current
            )
        );

    if (!currentSeason) {
      throw new Error(
        `Für ${league.name || league.id} wurde keine Current Season gefunden.`
      );
    }

    return {
      id:
        Number(
          currentSeason
            .season_id
        ),

      name:
        currentSeason
          .season_name ||
        `Season ${currentSeason.season_id}`,

      starting_at:
        currentSeason
          .starting_at ||
        null,

      ending_at:
        currentSeason
          .ending_at ||
        null,

      is_current:
        true,
    };
  }


  async function buildCurrentSeasonBaseStatsAllLeagues() {
    const leaguesToBuild =
      leagueCompetitions;

    if (!leaguesToBuild.length) {
      setSeasonBaseBuildResult({
        success: false,

        message:
          "Keine Ligawettbewerbe vorhanden.",
      });

      return;
    }

    setRunning(true);

    stopRequested.current =
      false;

    stopAfterLeagueRequested.current =
      false;

    setSeasonBaseBuildResult(
      null
    );

    setSeasonDiagnosticsResult(
      null
    );

    setSeasonBaseRecordResult(
      null
    );


    let processed = 0;
    let successful = 0;
    let failed = 0;

    const errors = [];


    try {
      for (
        let index = 0;
        index < leaguesToBuild.length;
        index += 1
      ) {
        if (
          stopRequested.current
        ) {
          break;
        }

        const league =
          leaguesToBuild[index];


        /*
         * Zuerst ausschließlich die von
         * Sportmonks als CURRENT markierte
         * Saison bestimmen.
         */
        let currentSeason;

        try {
          currentSeason =
            await getCurrentBuildSeasonForLeague(
              league
            );
        } catch (error) {
          processed += 1;
          failed += 1;

          errors.push({
            league_id:
              Number(
                league.id
              ),

            league_name:
              league.name ||
              null,

            message:
              error?.message ||
              String(error),
          });

          if (
            stopAfterLeagueRequested.current
          ) {
            break;
          }

          continue;
        }


        setProgress({
          current:
            index + 1,

          total:
            leaguesToBuild.length,

          leagueName:
            league?.name ||
            `League ${league.id}`,

          countryName:
            league?.country?.name ||
            "",

          action:
            "Aktuelle Saisons aller Ligen neu aufbauen",

          seasonCurrent:
            1,

          seasonTotal:
            1,

          seasonName:
            currentSeason.name,

          fixtureCurrent:
            0,

          fixtureTotal:
            0,
        });


        try {
          /*
           * Exakt derselbe bewährte
           * Single-Season-Builder wie beim
           * manuellen Admin-Button.
           *
           * Vorhandener Record wird bewusst
           * komplett auf den jetzigen Stand
           * neu aufgebaut.
           */
          const response =
            await fetchViaPost(
              "build-season-base-stats",
              JSON.stringify({
                league_id:
                  Number(
                    league.id
                  ),

                season_id:
                  Number(
                    currentSeason.id
                  ),
              }),
            );

          const result =
            await response.json();

          processed += 1;

          if (!result?.success) {
            failed += 1;

            errors.push({
              league_id:
                Number(
                  league.id
                ),

              league_name:
                league.name ||
                null,

              season_id:
                Number(
                  currentSeason.id
                ),

              season_name:
                currentSeason.name,

              message:
                result?.message ||
                "Current Season Base Record konnte nicht aufgebaut werden.",
            });
          } else {
            successful += 1;
          }

        } catch (error) {
          processed += 1;
          failed += 1;

          errors.push({
            league_id:
              Number(
                league.id
              ),

            league_name:
              league.name ||
              null,

            season_id:
              Number(
                currentSeason.id
              ),

            season_name:
              currentSeason.name,

            message:
              error?.message ||
              String(error),
          });
        }


        /*
         * Bei diesem Lauf entspricht eine
         * Work Unit exakt einer Liga.
         *
         * "Nach aktueller Liga abbrechen"
         * kann daher unmittelbar hier greifen.
         */
        if (
          stopAfterLeagueRequested.current
        ) {
          break;
        }
      }


      const stopped =
        stopRequested.current ||
        stopAfterLeagueRequested.current;


      let message =
        `${successful} aktuelle Saisonrecords neu aufgebaut, ` +
        `${failed} Fehler.`;

      if (stopped) {
        message +=
          " Lauf nach der zuletzt bearbeiteten Liga beendet.";
      }


      setSeasonBaseBuildResult({
        success:
          failed === 0,

        partial_success:
          successful > 0 &&
          failed > 0,

        message,

        mode:
          "rebuild_current_seasons",

        records_requested:
          leaguesToBuild.length,

        records_processed:
          processed,

        records_successful:
          successful,

        records_skipped:
          0,

        records_failed:
          failed,

        errors,

        stopped,
      });

    } catch (error) {
      setSeasonBaseBuildResult({
        success: false,

        message:
          error?.message ||
          String(error),
      });

    } finally {
      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",

        seasonCurrent: 0,
        seasonTotal: 0,
        seasonName: "",

        fixtureCurrent: 0,
        fixtureTotal: 0,
      });
    }
  }


  async function buildSeasonBaseStats(
    mode = "rebuild"
  ) {
    const missingOnly =
      mode === "missing_only";

    if (!selectedLeagues.length) {
      setSeasonBaseBuildResult({
        success: false,
        message:
          "Keine Ligawettbewerbe ausgewählt.",
      });
      return;
    }

    if (
      leagueId !== ALL_LEAGUES &&
      seasonId === NO_SEASON
    ) {
      setSeasonBaseBuildResult({
        success: false,
        message:
          "Bitte eine Saison oder „Alle verfügbaren Saisons“ auswählen.",
      });
      return;
    }

    setRunning(true);
    stopRequested.current = false;
    stopAfterLeagueRequested.current = false;

    setSeasonBaseBuildResult(null);
    setSeasonDiagnosticsResult(null);

    const workItems = [];
    const preparationErrors = [];

    try {
      /*
       * Zuerst vollständige Arbeitsliste erzeugen.
       * Dadurch kennen wir den Gesamtfortschritt
       * bereits vor dem eigentlichen Aufbau.
       */
      for (
        const league of
        selectedLeagues
      ) {
        if (stopRequested.current) {
          break;
        }

        let seasons = [];

        if (
          leagueId !== ALL_LEAGUES &&
          seasonId !== ALL_SEASONS &&
          seasonId !== NO_SEASON
        ) {
          seasons = [{
            id:
              Number(seasonId),

            name:
              leagueSeasons.find(
                (season) =>
                  Number(season.id) ===
                  Number(seasonId)
              )?.name ||
              `Season ${seasonId}`,
          }];
        } else {
          try {
            seasons =
              await getBuildSeasonsForLeague(
                league
              );
          } catch (error) {
            preparationErrors.push({
              league_id:
                Number(league.id),

              league_name:
                league.name ||
                null,

              message:
                error?.message ||
                String(error),
            });

            continue;
          }
        }

        seasons.forEach(
          (season, seasonIndex) => {
            workItems.push({
              league,
              season,
              seasonIndex,
              seasonTotal:
                seasons.length,
            });
          }
        );
      }

      if (!workItems.length) {
        setSeasonBaseBuildResult({
          success: false,

          message:
            "Keine Saisons zum Verarbeiten gefunden.",

          errors:
            preparationErrors,
        });

        return;
      }

      /*
       * Beim Mehrfachlauf keine einzelne alte
       * Tabelle währenddessen anzeigen.
       */
      if (
        workItems.length > 1
      ) {
        setSeasonBaseRecordResult(
          null
        );
      }

      let processed = 0;
      let successful = 0;
      let skipped = 0;
      let failed = 0;

      const errors = [
        ...preparationErrors,
      ];

      let lastSuccessfulRecord =
        null;

      for (
        let index = 0;
        index < workItems.length;
        index += 1
      ) {
        if (stopRequested.current) {
          break;
        }

        /*
         * "Nach aktueller Liga abbrechen":
         * Erst dann stoppen, wenn der nächste
         * WorkItem bereits zu einer anderen Liga
         * gehört. Damit bleibt die gerade laufende
         * Liga vollständig.
         */
        if (
          stopAfterLeagueRequested.current &&
          index > 0 &&
          Number(
            workItems[index - 1]
              ?.league
              ?.id
          ) !==
            Number(
              workItems[index]
                ?.league
                ?.id
            )
        ) {
          break;
        }

        const {
          league,
          season,
          seasonIndex,
          seasonTotal,
        } = workItems[index];

        const seasonName =
          season?.name ||
          `Season ${season.id}`;

        setProgress({
          current:
            index + 1,

          total:
            workItems.length,

          leagueName:
            league?.name ||
            `League ${league.id}`,

          countryName:
            league?.country?.name ||
            "",

          action:
            missingOnly
              ? "Fehlende Season Base Records"
              : "Season Base Records komplett neu aufbauen",

          seasonCurrent:
            seasonIndex + 1,

          seasonTotal,

          seasonName,

          fixtureCurrent: 0,

          fixtureTotal: 0,
        });


        /*
         * Fortsetzungsmodus:
         * Ein vorhandener Record wird vollständig
         * in Ruhe gelassen.
         */
        if (missingOnly) {
          try {
            const checkResponse =
              await fetchViaPost(
                "get-season-base-stats",
                JSON.stringify({
                  league_id:
                    Number(
                      league.id
                    ),

                  season_id:
                    Number(
                      season.id
                    ),
                }),
              );

            const existing =
              await checkResponse.json();

            if (
              existing?.success &&
              existing?.found
            ) {
              skipped += 1;
              processed += 1;
              continue;
            }
          } catch (error) {
            processed += 1;
            failed += 1;

            errors.push({
              league_id:
                Number(
                  league.id
                ),

              league_name:
                league.name ||
                null,

              season_id:
                Number(
                  season.id
                ),

              season_name:
                seasonName,

              message:
                "Vorhandener Record konnte nicht geprüft werden: " +
                (
                  error?.message ||
                  String(error)
                ),
            });

            continue;
          }
        }


        try {
          const response =
            await fetchViaPost(
              "build-season-base-stats",
              JSON.stringify({
                league_id:
                  Number(
                    league.id
                  ),

                season_id:
                  Number(
                    season.id
                  ),
              }),
            );

          const result =
            await response.json();

          processed += 1;

          if (!result?.success) {
            failed += 1;

            errors.push({
              league_id:
                Number(
                  league.id
                ),

              league_name:
                league.name ||
                null,

              season_id:
                Number(
                  season.id
                ),

              season_name:
                seasonName,

              message:
                result?.message ||
                "Season Base Record konnte nicht aufgebaut werden.",
            });

            continue;
          }

          successful += 1;

          if (result?.record) {
            lastSuccessfulRecord =
              result.record;
          }

          /*
           * Nur bei exakt einer Saison direkt
           * die neue Tabelle anzeigen.
           */
          if (
            workItems.length === 1 &&
            result?.record
          ) {
            setSeasonBaseRecordResult({
              success: true,
              found: true,
              record:
                result.record,
              message:
                "Season Base Record geladen.",
            });
          }
        } catch (error) {
          processed += 1;
          failed += 1;

          errors.push({
            league_id:
              Number(
                league.id
              ),

            league_name:
              league.name ||
              null,

            season_id:
              Number(
                season.id
              ),

            season_name:
              seasonName,

            message:
              error?.message ||
              String(error),
          });
        }
      }

      const stopped =
        stopRequested.current ||
        stopAfterLeagueRequested.current;

      let message =
        `${successful} Records neu aufgebaut`;

      if (missingOnly) {
        message +=
          `, ${skipped} vorhandene Records übersprungen`;
      }

      message +=
        `, ${failed} Fehler.`;

      if (stopped) {
        message +=
          " Lauf nach der zuletzt bearbeiteten Saison abgebrochen.";
      }

      setSeasonBaseBuildResult({
        success:
          failed === 0,

        partial_success:
          successful > 0 &&
          failed > 0,

        message,

        mode,

        records_requested:
          workItems.length,

        records_processed:
          processed,

        records_successful:
          successful,

        records_skipped:
          skipped,

        records_failed:
          failed,

        last_record:
          lastSuccessfulRecord,

        errors,

        stopped,
      });
    } catch (error) {
      setSeasonBaseBuildResult({
        success: false,

        message:
          error?.message ||
          String(error),
      });
    } finally {
      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",

        seasonCurrent: 0,
        seasonTotal: 0,
        seasonName: "",

        fixtureCurrent: 0,
        fixtureTotal: 0,
      });
    }
  }



  useEffect(() => {
    if (
      !open ||
      (
        activeSection !==
          "table_archive" &&
        activeSection !==
          "table_stats" &&
        activeSection !==
          "admin_table_stats"
      )
    ) {
      return;
    }

    loadSelectedSeasonBaseStats(
      leagueId,
      seasonId
    );

    loadSeasonTeams(seasonId);

    if (
      activeSection ===
      "table_stats"
    ) {
      loadUserSeasonStats(
        leagueId,
        seasonId
      );

      diagnoseExpectedTable(
        leagueId,
        seasonId
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    activeSection,
    leagueId,
    seasonId,
  ]);


  useEffect(() => {
    if (
      !open ||
      activeSection !== "table_stats" ||
      tableStatsView !== "team_statistics"
    ) {
      return;
    }

    loadTeamStatistics(
      leagueId,
      seasonId
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    activeSection,
    tableStatsView,
    leagueId,
    seasonId,
  ]);


  function recalculateFixtureOdds() {
    if (!selectedLeagues.length) {
      return;
    }

    if (hideFixtureOddsConfirmation) {
      startFixtureOddsBackfill();
      return;
    }

    setFixtureOddsConfirmOpen(true);
  }

  async function startFixtureOddsBackfill() {
    setFixtureOddsConfirmOpen(false);
    setRunning(true);
    stopRequested.current = false;
    setFixtureOddsResult(null);

    let fixturesFound = 0;
    let fixturesProcessed = 0;
    let fixtureOddsCreated = 0;
    let fixtureOddsUpdated = 0;
    let shouldGoalsAdded = 0;
    let shouldGoalsMissing = 0;

    const errors = [];

    try {
      for (let index = 0; index < selectedLeagues.length; index += 1) {
        if (stopRequested.current) {
          break;
        }

        const league = selectedLeagues[index];

        setProgress({
          current: index + 1,
          total: selectedLeagues.length,
          leagueName: league.name,
          countryName: league.country?.name || "",
          action: "Fixture Odds nachrüsten",
        });

        const result = await callFunction("enrich-league-fixture-odds", league);

        if (!result?.success) {
          errors.push({
            league_id: league.id,
            league_name: league.name,
            message: result?.message || "Unbekannter Fehler",
          });

          continue;
        }

        fixturesFound += Number(result.fixtures_found || 0);

        fixturesProcessed += Number(result.fixtures_processed || 0);

        fixtureOddsCreated += Number(result.fixture_odds_created || 0);

        fixtureOddsUpdated += Number(result.fixture_odds_updated || 0);

        shouldGoalsAdded += Number(result.should_goals_added || 0);

        shouldGoalsMissing += Number(result.should_goals_missing || 0);

        for (const error of result.errors || []) {
          errors.push({
            league_id: league.id,
            league_name: league.name,
            ...error,
          });
        }
      }

      setFixtureOddsResult({
        success: true,
        message:
          `${fixturesProcessed} von ` +
          `${fixturesFound} beendeten Spielen ` +
          `nachgerüstet.`,

        fixtures_found: fixturesFound,
        fixtures_processed: fixturesProcessed,

        fixture_odds_created: fixtureOddsCreated,

        fixture_odds_updated: fixtureOddsUpdated,

        should_goals_added: shouldGoalsAdded,

        should_goals_missing: shouldGoalsMissing,

        errors,
        stopped: stopRequested.current,
      });
    } catch (error) {
      setFixtureOddsResult({
        success: false,
        message: error?.message || String(error),
      });
    } finally {
      setRunning(false);

      setProgress({
        current: 0,
        total: 0,
        leagueName: "",
        countryName: "",
        action: "",
      });
    }
  }

  function handleSeasonChange(event) {
    /*
     * Beim Saisonwechsel darf weder eine verspätete
     * Antwort noch eine Erfolgsmeldung der vorherigen
     * Saison sichtbar bleiben.
     */
    seasonBaseRequestId.current += 1;
    leagueSeasonsRequestId.current += 1;

    setSeasonId(event.target.value);

    setSeasonBaseRecordResult(null);
    setSeasonBaseBuildResult(null);
    setSeasonDiagnosticsResult(null);

    setUserSeasonStatsState(null);
    setExpectedDiagnosis(null);

    setTeamStatisticsResult(null);
    setTeamStatisticsScope("overall");

    setTableStatsView("actual");
  }

  function handleLeagueChange(event) {
    /*
     * Noch laufende Requests der vorherigen Liga
     * dürfen danach nichts mehr in die UI schreiben.
     */
    seasonBaseRequestId.current += 1;
    leagueSeasonsRequestId.current += 1;

    setLeagueId(event.target.value);

    setSeasonId(
      activeSection === "table_archive" ||
      activeSection === "table_stats"
        ? NO_SEASON
        : ALL_SEASONS
    );
    setTransitionResult(null);
    setTransitionSummaryResult(null);
    setClearTransitionResult(null);
    setLeagueReferenceResult(null);
    setFixtureOddsResult(null);
    setSeasonDiagnosticsResult(null);
    setSeasonBaseRecordResult(null);
    setSeasonBaseBuildResult(null);

    setUserSeasonStatsState(null);
    setExpectedDiagnosis(null);

    setTeamStatisticsResult(null);
    setTeamStatisticsScope("overall");

    setTableStatsView("actual");
  }

  function handleClose() {
    if (!running && !historyRunning) {
      setOpen(false);
    }
  }

  const leagueGoalAverage =
    transitionResult?.league_summary?.goal_average ??
    leagueReferenceResult?.aggregate?.actual?.goal_average ??
    null;

  const progressPercent =
    progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={
        activeSection === "admin_table_stats" ||
        activeSection === "table_archive" ||
        activeSection === "table_stats"
          ? "xl"
          : "md"
      }
    >
      <DialogTitle>
        Statistics – {sectionTitles[activeSection] || "Statistics"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography>
            {activeSection === "official_tables" &&
              "Offizielle Ligatabellen, wie sie von Sportmonks bereitgestellt werden."}
            {activeSection === "league_reference" &&
              "League-wide reference values: expected values, actual results and their comparison."}

            {activeSection === "team_transition" &&
              "Statistics for promoted and relegated teams across seasons."}

            {activeSection === "fixture_stats" &&
              "Completeness and enrichment of Fixture Odds, including Pre-Match/Post-Match classification and Should Goals."}

            {activeSection === "table_archive" &&
              "Stored league tables from current and previous seasons."}

            {(activeSection === "table_stats" ||
              activeSection === "admin_table_stats") &&
              "Hier liegen die von betty aufgebauten Saisonstatistiken: reale Entwicklung, eigene Expected-Werte, Should-Werte und historische Vergleiche."}
          </Typography>

          {activeSection === "official_tables" && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Offizielle Tabellen</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Liga auswählen, um die aktuell von Sportmonks gelieferte Tabelle zu öffnen.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {leagueCompetitions.map((league) => (
                  <Button
                    key={league.id}
                    variant="outlined"
                    onClick={() => onOpenOfficialStandings?.(league)}
                  >
                    <LeagueDisplay league={league} compact />
                  </Button>
                ))}
              </Stack>
            </Paper>
          )}

          {activeSection === "table_stats" && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6">HISTORISCHE DATENPFLEGE</Typography>
                  <Typography color="text.secondary">
                    Prüfe, ob betty Statistiken aus früheren Saisons noch ergänzen kann.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="outlined" disabled={historyRunning} onClick={() => previewHistoricalData()}>
                    {historyPreview ? "Erneut prüfen" : "Historische Daten prüfen"}
                  </Button>
                  {historyPreview?.summary?.repairable > 0 && !historyResumeCursor && (
                    <Button variant="contained" disabled={historyRunning} onClick={() => setActionConfirmation({
                      title: "Historische Statistiken ergänzen",
                      paragraphs: ["betty ergänzt historische Statistiken, die aus deinen bereits vorhandenen Daten wiederhergestellt werden können. Bestehende Tabellen werden nicht verändert."],
                      confirmLabel: `${historyPreview.summary.repairable} ${historyPreview.summary.repairable === 1 ? "Saison" : "Saisons"} ergänzen`,
                      action: completeHistoricalData,
                    })}>
                      {historyPreview.summary.repairable} {historyPreview.summary.repairable === 1 ? "Saison" : "Saisons"} ergänzen
                    </Button>
                  )}
                  {historyResumeCursor && (
                    <Button variant="contained" color="warning" disabled={historyRunning} onClick={completeHistoricalData}>
                      Verarbeitung fortsetzen
                    </Button>
                  )}
                </Stack>
                {historyRunning && (
                  <Box>
                    <LinearProgress />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      {historyProgress.current} von {historyProgress.total || "…"} Saisons verarbeitet
                    </Typography>
                  </Box>
                )}
                {historyPreview && !historyRunning && (
                  <Alert severity={historyPreview.summary.repairable > 0 ? "info" : "success"}>
                    {historyPreview.summary.repairable > 0
                      ? `Für ${historyPreview.summary.repairable} ${historyPreview.summary.repairable === 1 ? "frühere Saison können" : "frühere Saisons können"} zusätzliche Statistiken ergänzt werden.`
                      : "Alles aktuell. Es gibt nichts zu ergänzen."}
                  </Alert>
                )}
                {historyOutcome && (
                  <Alert severity={historyOutcome.success ? "success" : "warning"}>
                    {historyOutcome.message}
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}

          {activeSection !== "official_tables" && <FormControl sx={{ width: "100%", maxWidth: activeSection === "table_stats" ? 360 : 640 }}>
            <InputLabel id="statistics-league-label">League</InputLabel>

            <Select
              labelId="statistics-league-label"
              value={leagueId}
              label="League"
              disabled={running}
              onChange={handleLeagueChange}
            >
              {(
                activeSection === "table_archive" ||
                activeSection === "table_stats"
              ) ? (
                <MenuItem
                  value={NO_LEAGUE}
                  disabled
                >
                  Liga auswählen …
                </MenuItem>
              ) : (
                <MenuItem value={ALL_LEAGUES}>
                  Alle Ligawettbewerbe
                </MenuItem>
              )}

              {leagueCompetitions.map((league) => (
                <MenuItem
                  key={league.id}
                  value={Number(league.id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <LeagueDisplay
                      league={league}
                      compact
                    />

                    {activeSection === "admin_table_stats" && <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        ml: "auto",
                        fontVariantNumeric:
                          "tabular-nums",
                      }}
                    >
                      League ID {league.id}
                    </Typography>}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>}

          {(activeSection === "league_reference" ||
            activeSection === "table_archive" ||
            activeSection === "table_stats" ||
            activeSection === "admin_table_stats") &&
            leagueId !== ALL_LEAGUES &&
            leagueId !== NO_LEAGUE && (
            <FormControl sx={{ width: "100%", maxWidth: activeSection === "table_stats" ? 240 : 640 }}>
              <InputLabel id="statistics-season-label">Season</InputLabel>

              <Select
                labelId="statistics-season-label"
                value={seasonId}
                label="Season"
                disabled={running || loadingSeasons}
                onChange={handleSeasonChange}
              >
                {(
                  activeSection === "table_archive" ||
                  activeSection === "table_stats"
                ) ? (
                  <MenuItem
                    value={NO_SEASON}
                    disabled
                  >
                    Saison auswählen …
                  </MenuItem>
                ) : (
                  <MenuItem value={ALL_SEASONS}>
                    Alle verfügbaren Saisons
                  </MenuItem>
                )}

                {leagueSeasons.map((season) => (
                  <MenuItem
                    key={season.id}
                    value={Number(season.id)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Typography>
                        {season.name ||
                          `Season ${season.id}`}
                        {season.starting_at
                          ? ` – ${String(
                              season.starting_at
                            ).slice(0, 10)}`
                          : ""}
                      </Typography>

                      {activeSection === "admin_table_stats" && <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          ml: "auto",
                          fontVariantNumeric:
                            "tabular-nums",
                        }}
                      >
                        Season ID {season.id}
                      </Typography>}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(activeSection === "table_archive" ||
            activeSection === "table_stats" ||
            activeSection === "admin_table_stats") &&
            leagueId !== ALL_LEAGUES &&
            leagueId !== NO_LEAGUE &&
            seasonId !== ALL_SEASONS &&
            seasonId !== NO_SEASON &&
            loadingSeasonBaseRecord && (
              <LinearProgress />
            )}

          {activeSection === "admin_table_stats" &&
            leagueId !== ALL_LEAGUES &&
            seasonId !== ALL_SEASONS &&
            seasonBaseRecordResult?.success &&
            !seasonBaseRecordResult?.found && (
              <Alert severity="info">
                Season Base Record noch nicht vorhanden.
                Du kannst ihn unten mit
                „Season Base Record neu aufbauen“
                erzeugen.
              </Alert>
            )}

          {(activeSection === "table_archive" ||
            activeSection === "table_stats" ||
            activeSection === "admin_table_stats") &&
            seasonBaseRecordResult &&
            !seasonBaseRecordResult.success && (
              <Alert severity="error">
                {activeSection === "table_stats"
                  ? "Die reale Tabelle konnte für diese Saison nicht geladen werden."
                  : seasonBaseRecordResult.message}
              </Alert>
            )}

          {activeSection ===
            "table_stats" &&
            leagueId !== NO_LEAGUE &&
            seasonId !== NO_SEASON && (
              <Paper
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      {i18next.t(
                        "help.tableStats.title"
                      )}
                    </Typography>

                    <Typography
                      color="text.secondary"
                    >
                      {i18next.t(
                        "help.tableStats.description"
                      )}
                    </Typography>
                  </Box>

                  {loadingUserSeasonStats && (
                    <LinearProgress />
                  )}


                  {userSeasonStatsState &&
                    !userSeasonStatsState.success && (
                      <Alert severity="error">
                        Die persönlichen Tabellen konnten für diese Saison nicht geladen werden.
                      </Alert>
                    )}


                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ display: "none" }}
                  >
                    <Button
                      variant="outlined"
                      disabled={
                        loadingUserSeasonStats ||
                        compilingMissingFixtures
                      }
                      onClick={
                        diagnoseExpectedTable
                      }
                    >
                      {i18next.t(
                        "help.tableStats.checkData"
                      )}
                    </Button>

                    {(
                      expectedDiagnosis
                        ?.expected_table_inconsistent ||
                      shouldDiagnosis
                        ?.should_table_inconsistent
                    ) && (
                      <Button
                        variant="contained"
                        color="warning"
                        disabled={
                          repairingTables ||
                          loadingUserSeasonStats
                        }
                        onClick={
                          repairUserTables
                        }
                      >
                        {repairingTables
                          ? i18next.t(
                              "help.tableStats.repairing"
                            )
                          : i18next.t(
                              "help.tableStats.repairTables"
                            )}
                      </Button>
                    )}
                  </Stack>

                  {tableStatsView === "expected" &&
                    userSeasonStatsState?.expected_data_source === "reference" && (
                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2">Historische Referenz</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Für diese Saison sind keine eigenen Expected-Werte vorhanden. Angezeigt werden die damaligen betty-Referenzwerte.
                      </Typography>
                    </Box>
                  )}

                  {!loadingUserSeasonStats && userSeasonStatsState?.success && (
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.4, borderRadius: 2, bgcolor: "#17242d", color: "white", boxShadow: "0 8px 24px rgba(23,36,45,.16)" }}>
                        {selectedLeague?.image_path && <img src={selectedLeague.image_path} alt="" width="42" height="42" style={{ objectFit: "contain" }} />}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h5" sx={{ fontWeight: 900 }}>{selectedLeague?.name || userSeasonStatsState?.record?.league_name}</Typography>
                          <Typography sx={{ color: "rgba(255,255,255,.72)" }}>{leagueSeasons.find((item) => Number(item.id) === Number(seasonId))?.name || userSeasonStatsState?.record?.season_name}</Typography>
                          {isAdmin && <Typography variant="caption" sx={{ color: "rgba(255,255,255,.55)" }}>League ID {leagueId} · Season ID {seasonId}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Tooltip title={i18next.t("help.leagueSeasonComparison.seasonDataMaintenanceTooltip")}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={seasonDataRunning}
                              onClick={() => maintainSeasonData("diagnose_season_data")}
                            >
                              {i18next.t("help.leagueSeasonComparison.seasonDataCheck")}
                            </Button>
                          </span>
                        </Tooltip>
                        {seasonDataMaintenance?.success && seasonDataMaintenance?.overall_status === "build_required" && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={seasonDataRunning}
                            onClick={() => setActionConfirmation({
                              title: i18next.t("help.leagueSeasonComparison.seasonDataBuildMissing"),
                              paragraphs: [i18next.t("help.leagueSeasonComparison.seasonDataBuildMissingConfirmation")],
                              confirmLabel: i18next.t("help.leagueSeasonComparison.seasonDataBuildMissing"),
                              action: () => maintainSeasonData("build_missing_season_data"),
                            })}
                          >
                            {i18next.t("help.leagueSeasonComparison.seasonDataBuildMissing")}
                          </Button>
                        )}
                        <Button size="small" variant="text" disabled={seasonDataRunning} onClick={() => setActionConfirmation({
                          title: i18next.t("help.leagueSeasonComparison.seasonDataRebuild"),
                          paragraphs: [i18next.t("help.leagueSeasonComparison.seasonDataRebuildConfirmation")],
                          confirmLabel: i18next.t("help.leagueSeasonComparison.seasonDataRebuild"),
                          action: () => maintainSeasonData("rebuild_season_data"),
                        })}>{i18next.t("help.leagueSeasonComparison.seasonDataRebuild")}</Button>
                        {seasonDataRunning && <CircularProgress size={20} />}
                      </Box>
                      {seasonDataMaintenance && !seasonDataRunning && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          {!seasonDataMaintenance.success ? (
                            <Typography color="error.main" variant="body2">{seasonDataMaintenance.message}</Typography>
                          ) : <Stack spacing={0.35}>
                            <Typography variant="body2"><b>Perfect Game:</b> {seasonDataMaintenance.perfect_game.calculated_count} / {seasonDataMaintenance.perfect_game.eligible_count} {i18next.t(seasonDataMaintenance.perfect_game.current ? "help.leagueSeasonComparison.seasonDataCurrent" : "help.leagueSeasonComparison.seasonDataBuildRequired")}</Typography>
                            <Typography variant="body2"><b>{i18next.t("help.leagueSeasonComparison.marketGoalExpectations")}:</b> {seasonDataMaintenance.market_derived.built_count} / {seasonDataMaintenance.market_derived.eligible_count}</Typography>
                            {seasonDataMaintenance.market_derived.market_data_missing_count > 0 && <Typography variant="body2">{i18next.t("help.leagueSeasonComparison.marketDataMissing", { count: seasonDataMaintenance.market_derived.market_data_missing_count })}</Typography>}
                            {seasonDataMaintenance.market_derived.reconstructed_basis_count > 0 && <Typography variant="body2">{i18next.t("help.leagueSeasonComparison.marketModelBasisReconstructed", { count: seasonDataMaintenance.market_derived.reconstructed_basis_count })}</Typography>}
                            {seasonDataMaintenance.market_derived.model_basis_missing_count > 0 && <Typography variant="body2">{i18next.t("help.leagueSeasonComparison.marketModelBasisMissing", { count: seasonDataMaintenance.market_derived.model_basis_missing_count })}</Typography>}
                            {seasonDataMaintenance.market_derived.source_error_count > 0 && <Typography variant="body2">{i18next.t("help.leagueSeasonComparison.marketSourceError", { count: seasonDataMaintenance.market_derived.source_error_count })}</Typography>}
                            <Typography variant="body2"><b>{i18next.t("help.leagueSeasonComparison.marketTeamValues")}:</b> {i18next.t(seasonDataMaintenance.market_aggregates.current ? "help.leagueSeasonComparison.seasonDataCurrent" : "help.leagueSeasonComparison.seasonDataIncomplete")}</Typography>
                            <Typography variant="body2"><b>{i18next.t("help.leagueSeasonComparison.seasonDataOverall")}:</b> {i18next.t(seasonDataMaintenance.overall_status === "current" ? "help.leagueSeasonComparison.seasonDataCurrent" : "help.leagueSeasonComparison.seasonDataBuildRequired")}</Typography>
                            {isAdmin && seasonDataMaintenance.details?.length > 0 && <details><summary>{i18next.t("help.leagueSeasonComparison.seasonDataDetails")}</summary><Typography component="pre" variant="caption" sx={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(seasonDataMaintenance.details, null, 2)}</Typography></details>}
                          </Stack>}
                        </Paper>
                      )}
                      <Paper variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, overflowX: "auto", borderRadius: 2, boxShadow: "0 5px 18px rgba(23,36,45,.08)" }}>
                        <CompactLeagueTable
                          seasonId={seasonId}
                          actualRows={seasonBaseRecordResult?.record?.actual_table || []}
                          expectedRows={userSeasonStatsState?.record?.expected_table || []}
                          shouldRows={userSeasonStatsState?.record?.should_table || []}
                          onViewChange={setTableStatsView}
                          teamLogos={new Map(seasonTeams.map((team) => [Number(team.id), team.image_path || team.logo_path]))}
                          standingRules={seasonBaseRecordResult?.record?.standing_rules?.items || []}
                          transitionItems={seasonBaseRecordResult?.record?.team_transitions?.items || []}
                          teamSettings={userSeasonStatsState?.team_settings}
                        />
                      </Paper>
                      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 2 }, overflowX: "auto", borderRadius: 2, bgcolor: "#f7f9fa", boxShadow: "0 5px 18px rgba(23,36,45,.08)" }}>
                        <LeagueSeasonComparison data={userSeasonStatsState} />
                      </Paper>
                    </Stack>
                  )}

                  {tableRepairResult && (
                    <Alert
                      severity={
                        tableRepairResult.success
                          ? "success"
                          : "error"
                      }
                    >
                      {tableRepairResult.message}
                    </Alert>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ display: "none" }}
                  >

                    {expectedDiagnosis?.success &&
                      expectedDiagnosis?.possible &&
                      Number(
                        expectedDiagnosis
                          ?.fixtures_missing_expected_values ||
                        0
                      ) > 0 && (
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={
                            compilingMissingFixtures
                          }
                          onClick={
                            compileMissingExpectedFixtures
                          }
                        >
                          Compile missing fixtures
                        </Button>
                      )}

                    {expectedDiagnosis?.success &&
                      expectedDiagnosis?.possible &&
                      Number(
                        expectedDiagnosis
                          ?.fixtures_missing_expected_values ||
                        0
                      ) === 0 && (
                        <Button
                          variant="contained"
                          disabled={
                            loadingUserSeasonStats ||
                            compilingMissingFixtures
                          }
                          onClick={
                            calculateExpectedTable
                          }
                        >
                          {userSeasonStatsState
                            ?.record
                            ?.expected_table
                            ? "Update Expected Table"
                            : "Calculate Expected Table"}
                        </Button>
                      )}

                    {shouldDiagnosis?.success &&
                      shouldDiagnosis
                        ?.can_build_should && (
                        <Button
                          variant="contained"
                          disabled={
                            loadingUserSeasonStats ||
                            compilingMissingFixtures
                          }
                          onClick={
                            calculateShouldTable
                          }
                        >
                          {userSeasonStatsState
                            ?.record
                            ?.should_table
                            ? "Update Should Table"
                            : "Calculate Should Table"}
                        </Button>
                      )}
                  </Stack>


                  {compilingMissingFixtures && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5 }}
                    >
                      <Typography
                        gutterBottom
                      >
                        Compiling missing fixtures:{" "}
                        {compileMissingProgress.current}
                        {" of "}
                        {compileMissingProgress.total}
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={
                          compileMissingProgress.total > 0
                            ? (
                                compileMissingProgress.current /
                                compileMissingProgress.total
                              ) * 100
                            : 0
                        }
                      />

                      <Button
                        sx={{ mt: 1 }}
                        color="warning"
                        onClick={
                          requestStop
                        }
                      >
                        Stop after current fixture
                      </Button>
                    </Paper>
                  )}


                </Stack>
              </Paper>
            )}


          {activeSection ===
            "table_stats" &&
            tableStatsView ===
              "team_statistics" && (
              <>
                {loadingTeamStatistics && (
                  <LinearProgress />
                )}

                {!loadingTeamStatistics &&
                  teamStatisticsResult && (
                    <TeamStatisticsPanel
                      result={
                        teamStatisticsResult
                      }
                      scope={
                        teamStatisticsScope
                      }
                      setScope={
                        setTeamStatisticsScope
                      }
                    />
                  )}

                {!loadingTeamStatistics &&
                  !teamStatisticsResult && (
                    <Alert severity="info">
                      Team Statistics werden geladen,
                      sobald Liga und Saison ausgewählt
                      sind.
                    </Alert>
                  )}
              </>
            )}


          {activeSection ===
            "admin_table_stats" &&
            seasonBaseRecordResult?.success &&
            seasonBaseRecordResult?.found && (
              <SeasonBaseTable
                result={
                  seasonBaseRecordResult
                }
              />
            )}


          {activeSection ===
            "table_archive" &&
            leagueId !== NO_LEAGUE &&
            seasonId !== NO_SEASON &&
            seasonBaseRecordResult?.success &&
            !seasonBaseRecordResult?.found && (
              <Alert severity="info">
                No stored table is available for this season.
              </Alert>
            )}


          {activeSection ===
            "table_archive" &&
            seasonBaseRecordResult?.success &&
            seasonBaseRecordResult?.found && (
              <SeasonBaseTable
                result={
                  seasonBaseRecordResult
                }
              />
            )}

          {activeSection === "team_transition" && (
            <Button
              variant="contained"
              disabled={running || !selectedLeagues.length}
              onClick={recalculateTeamTransitionStats}
            >
              Recalculate Team Transition Stats
            </Button>
          )}

          {activeSection === "fixture_stats" && (
            <Button
              variant="contained"
              color="secondary"
              disabled={running || !selectedLeagues.length}
              onClick={recalculateFixtureOdds}
              title={
                "Ergänzt Fixture-Odds-Datensätze um " +
                "Berechnungsart und Should Goals. " +
                "Der vollständige Liga-Durchlauf wird " +
                "im nächsten Schritt ergänzt."
              }
            >
              Recalculate Fixture Odds
            </Button>
          )}

          {activeSection === "team_transition" && (
            <Button
              variant="outlined"
              disabled={running}
              onClick={loadTeamTransitionSummary}
            >
              Load Team Transition Summary
            </Button>
          )}

          {activeSection === "team_transition" && (
            <Button
              variant="outlined"
              color="error"
              disabled={running}
              onClick={clearTeamTransitionStats}
            >
              Clear Team Transition Stats
            </Button>
          )}

          {activeSection === "admin_table_stats" && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Season Base Diagnostics
              </Typography>

              <Typography sx={{ mb: 1 }}>
                Die Diagnose prüft zunächst nur die saisonbezogene Datenbasis.
              </Typography>

              <Typography sx={{ mb: 2 }}>
                Sie ermittelt verfügbare Saisons, Fixtures, abgeschlossene
                Spiele, Ergebnisse und die von Sportmonks gelieferten
                Stage-Strukturen. Es werden dabei noch keine Season-Base-Records
                geschrieben.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Auch „Alle Ligawettbewerbe“ kann diagnostiziert werden. Der Lauf
                verändert keine vorhandenen Statistikdaten.
              </Alert>

              <Stack
                direction="row"
                spacing={1.5}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  variant="contained"
                  disabled={
                    running ||
                    !selectedLeagues.length
                  }
                  onClick={
                    runSeasonBaseDiagnostics
                  }
                >
                  Diagnose starten
                </Button>

                <Button
                  variant="outlined"
                  disabled={
                    running ||
                    !selectedLeagues.length ||
                    (
                      leagueId !== ALL_LEAGUES &&
                      seasonId === NO_SEASON
                    )
                  }
                  onClick={() =>
                    buildSeasonBaseStats(
                      "rebuild"
                    )
                  }
                >
                  {leagueId === ALL_LEAGUES
                    ? "Alle Season Base Records komplett neu aufbauen"
                    : seasonId === ALL_SEASONS
                    ? "Alle Saisons dieser Liga neu aufbauen"
                    : "Season Base Record neu aufbauen"}
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={
                    running ||
                    !selectedLeagues.length ||
                    (
                      leagueId !== ALL_LEAGUES &&
                      seasonId === NO_SEASON
                    )
                  }
                  onClick={() =>
                    buildSeasonBaseStats(
                      "missing_only"
                    )
                  }
                >
                  Nur fehlende Records aufbauen
                </Button>

                <Button
                  variant="outlined"
                  disabled={
                    running ||
                    !selectedLeagues.length
                  }
                  onClick={
                    enrichExistingSeasonBaseStats
                  }
                >
                  Bestehende Records anreichern
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    running ||
                    !leagueCompetitions.length
                  }
                  onClick={
                    buildCurrentSeasonBaseStatsAllLeagues
                  }
                >
                  Aktuelle Saisons aller Ligen neu aufbauen
                </Button>
              </Stack>
            </Paper>
          )}

          {activeSection === "admin_table_stats" &&
            seasonBaseBuildResult && (
              <Alert
                severity={
                  seasonBaseBuildResult.success
                    ? "success"
                    : "error"
                }
              >
                {seasonBaseBuildResult.message}
              </Alert>
            )}

          {activeSection === "admin_table_stats" &&
            seasonDiagnosticsResult &&
            !seasonDiagnosticsResult.success && (
              <Alert severity="error">
                {seasonDiagnosticsResult.message ||
                  "Season Base Diagnostics fehlgeschlagen."}
              </Alert>
            )}

          {activeSection === "admin_table_stats" && seasonDiagnosticsResult?.success && (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Diagnose – Übersicht
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 0.75,
                  }}
                >
                  <Row
                    label="Ligen geprüft"
                    value={seasonDiagnosticsResult.summary?.leagues_diagnosed}
                  />

                  <Row
                    label="Saisons geprüft"
                    value={seasonDiagnosticsResult.summary?.seasons_diagnosed}
                  />

                  <Row
                    label="Fixtures gefunden"
                    value={seasonDiagnosticsResult.summary?.fixtures_total}
                  />

                  <Row
                    label="Beendet mit Ergebnis"
                    value={seasonDiagnosticsResult.summary?.finished_with_score}
                  />

                  <Row
                    label="Fixtures in der Zukunft"
                    value={seasonDiagnosticsResult.summary?.future_fixtures}
                  />

                  <Row
                    label="Historische Auffälligkeiten"
                    value={
                      seasonDiagnosticsResult.summary
                        ?.historical_unexpected_state
                    }
                  />

                  <Row
                    label="Historisch: Ergebnis vorhanden"
                    value={
                      seasonDiagnosticsResult.summary
                        ?.historical_unexpected_state_with_score
                    }
                  />

                  <Row
                    label="Historisch: Ergebnis fehlt"
                    value={
                      seasonDiagnosticsResult.summary
                        ?.historical_unexpected_state_without_score
                    }
                  />

                  <Row
                    label="Regular-Stage-Kandidaten"
                    value={
                      seasonDiagnosticsResult.summary
                        ?.regular_candidate_fixtures
                    }
                  />

                  <Row
                    label="Fehler"
                    value={seasonDiagnosticsResult.errors?.length || 0}
                  />
                </Box>
              </Paper>

              {seasonDiagnosticsResult.leagues?.map((league) => (
                <Paper key={league.league_id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {league.league_name}
                  </Typography>

                  <Typography sx={{ mb: 2 }}>
                    {league.season_count} Saisons diagnostiziert
                  </Typography>

                  <Stack spacing={1.5}>
                    {(league.seasons || []).map((season) => (
                      <Paper
                        key={season.season_id}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          {season.season_name || `Season ${season.season_id}`}
                          {" – ID "}
                          {season.season_id}
                        </Typography>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 0.5,
                          }}
                        >
                          <Row
                            label="Fixtures gesamt"
                            value={season.fixtures?.total}
                          />

                          <Row
                            label="Beendet"
                            value={season.fixtures?.finished}
                          />

                          <Row
                            label="Beendet mit Ergebnis"
                            value={season.fixtures?.finished_with_score}
                          />

                          <Row
                            label="Beendet ohne Ergebnis"
                            value={season.fixtures?.finished_without_score}
                          />

                          <Row
                            label="In der Zukunft"
                            value={season.fixtures?.future}
                          />

                          <Row
                            label="Historische Auffälligkeiten"
                            value={
                              season.fixtures
                                ?.historical_unexpected_state
                            }
                          />

                          <Row
                            label="Davon mit Ergebnis"
                            value={
                              season.fixtures
                                ?.historical_unexpected_state_with_score
                            }
                          />

                          <Row
                            label="Davon ohne Ergebnis"
                            value={
                              season.fixtures
                                ?.historical_unexpected_state_without_score
                            }
                          />

                          <Row
                            label="Regular-Stage-Kandidaten"
                            value={
                              season.scope_preview
                                ?.regular_candidate_fixture_count
                            }
                          />
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            mt: 1.5,
                            mb: 0.5,
                          }}
                        >
                          Stages
                        </Typography>

                        {(season.stages || []).map((stage) => (
                          <Typography
                            key={stage.stage_id ?? "none"}
                            variant="body2"
                          >
                            {stage.stage_name || "Stage ohne Namen"}
                            {" · ID "}
                            {stage.stage_id ?? "–"}
                            {" · Type "}
                            {stage.stage_type_id ?? "–"}
                            {" · Fixtures "}
                            {stage.fixtures_total}
                            {" · beendet "}
                            {stage.finished}
                          </Typography>
                        ))}

                        {!season.diagnostics?.all_finished_have_score && (
                          <Alert severity="warning" sx={{ mt: 1.5 }}>
                            Mindestens ein abgeschlossenes Fixture besitzt kein
                            auswertbares Ergebnis.
                          </Alert>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              ))}
            </>
          )}

          {running && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600 }}
                gutterBottom
              >
                {progress.action}
              </Typography>

              {activeSection === "admin_table_stats" &&
              progress.seasonTotal > 0 ? (
                <>
                  <Typography gutterBottom>
                    {progress.leagueName}
                    {progress.countryName
                      ? ` – ${progress.countryName}`
                      : ""}
                  </Typography>

                  <Typography gutterBottom>
                    Record {progress.current} von{" "}
                    {progress.total}
                  </Typography>

                  <Typography gutterBottom>
                    Saison {progress.seasonCurrent} von{" "}
                    {progress.seasonTotal}:{" "}
                    {progress.seasonName}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography gutterBottom>
                    Liga {progress.current} von {progress.total}
                  </Typography>

                  <Typography gutterBottom>
                    {progress.leagueName}
                    {progress.countryName
                      ? ` – ${progress.countryName}`
                      : ""}
                  </Typography>
                </>
              )}

              {activeSection === "league_reference" &&
                progress.seasonTotal > 0 && (
                  <>
                    <Typography gutterBottom>
                      Saison {progress.seasonCurrent} von {progress.seasonTotal}
                      : {progress.seasonName}
                    </Typography>

                    <Typography gutterBottom>
                      Spiel {progress.fixtureCurrent} von{" "}
                      {progress.fixtureTotal}
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={
                        progress.fixtureTotal > 0
                          ? (progress.fixtureCurrent / progress.fixtureTotal) *
                            100
                          : 0
                      }
                    />
                  </>
                )}

              {activeSection !== "league_reference" && (
                <LinearProgress variant="determinate" value={progressPercent} />
              )}

              {activeSection === "admin_table_stats" ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button
                    color="warning"
                    onClick={requestStop}
                  >
                    Nach aktueller Saison abbrechen
                  </Button>

                  <Button
                    color="warning"
                    variant="outlined"
                    onClick={requestStopAfterLeague}
                  >
                    Nach aktueller Liga abbrechen
                  </Button>
                </Stack>
              ) : (
                <Button
                  sx={{ mt: 2 }}
                  color="warning"
                  onClick={requestStop}
                >
                  Nach aktuellem Spiel abbrechen
                </Button>
              )}
            </Paper>
          )}

          {activeSection === "fixture_stats" &&
            fixtureOddsResult &&
            !fixtureOddsResult.success && (
              <Alert severity="error">{fixtureOddsResult.message}</Alert>
            )}

          {activeSection === "fixture_stats" && fixtureOddsResult?.success && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Alert severity="success">{fixtureOddsResult.message}</Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 0.75,
                  mt: 2,
                }}
              >
                <Row
                  label="Beendete Spiele gefunden"
                  value={fixtureOddsResult.fixtures_found}
                />

                <Row
                  label="Erfolgreich nachgerüstet"
                  value={fixtureOddsResult.fixtures_processed}
                />

                <Row
                  label="Fixture Odds neu angelegt"
                  value={fixtureOddsResult.fixture_odds_created}
                />

                <Row
                  label="Fixture Odds ergänzt"
                  value={fixtureOddsResult.fixture_odds_updated}
                />

                <Row
                  label="Should Goals ergänzt"
                  value={fixtureOddsResult.should_goals_added}
                />

                <Row
                  label="Should Goals nicht verfügbar"
                  value={fixtureOddsResult.should_goals_missing}
                />

                <Row
                  label="Fehler"
                  value={fixtureOddsResult.errors?.length || 0}
                />
              </Box>
            </Paper>
          )}

          {clearTransitionResult && !clearTransitionResult.success && (
            <Alert severity="error">{clearTransitionResult.message}</Alert>
          )}

          {clearTransitionResult?.success && (
            <Alert severity="success">{clearTransitionResult.message}</Alert>
          )}

          {activeSection === "team_transition" &&
            transitionSummaryResult &&
            !transitionSummaryResult.success && (
              <Alert severity="error">{transitionSummaryResult.message}</Alert>
            )}

          {activeSection === "team_transition" &&
            transitionSummaryResult?.success && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Team Transition Summary
                </Typography>

                <Typography gutterBottom>
                  {transitionSummaryResult.message}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 0.75,
                  }}
                >
                  <Row
                    label="Datensätze insgesamt"
                    value={transitionSummaryResult.records_total}
                  />

                  <Row
                    label="Datensätze passend"
                    value={transitionSummaryResult.records_matching}
                  />

                  <Row
                    label="Aufsteiger"
                    value={transitionSummaryResult.promoted}
                  />

                  <Row
                    label="Absteiger"
                    value={transitionSummaryResult.relegated}
                  />

                  <Row
                    label="Nicht klassifiziert"
                    value={transitionSummaryResult.unclassified}
                  />

                  <Row
                    label="Lesefehler"
                    value={transitionSummaryResult.read_errors}
                  />
                </Box>
              </Paper>
            )}

          {activeSection === "team_transition" &&
            transitionResult &&
            !transitionResult.success && (
              <Alert severity="error">{transitionResult.message}</Alert>
            )}

          {activeSection === "team_transition" && transitionResult?.success && (
            <>
              <Alert severity="success">
                <strong>
                  {leagueId === ALL_LEAGUES
                    ? "Alle Ligen"
                    : selectedLeague?.name || "League"}
                </strong>
                <br />
                {transitionResult.message}
              </Alert>

              <TransitionSummary
                title="Aufsteiger"
                summary={transitionResult.promoted_summary}
                leagueGoalAverage={leagueGoalAverage}
              />

              <TransitionSummary
                title="Absteiger"
                summary={transitionResult.relegated_summary}
                leagueGoalAverage={leagueGoalAverage}
              />
            </>
          )}

          {activeSection === "league_reference" &&
            leagueReferenceResult &&
            !leagueReferenceResult.success && (
              <Alert severity="error">{leagueReferenceResult.message}</Alert>
            )}

          {activeSection === "league_reference" &&
            leagueReferenceResult?.success && (
              <>
                <Alert severity="success">
                  <strong>
                    {leagueId === ALL_LEAGUES
                      ? "Alle Ligen"
                      : selectedLeague?.name || "League"}
                  </strong>
                  <br />
                  {leagueReferenceResult.message}
                </Alert>

                <LeagueReferenceSummary result={leagueReferenceResult} />

                {leagueReferenceResult.aggregate?.should_goals_comparison
                  ?.diagnostics && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Should Goals – Diagnose
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 0.75,
                      }}
                    >
                      <Row
                        label="Versuchte Fixtures"
                        value={
                          leagueReferenceResult.aggregate
                            .should_goals_comparison.diagnostics
                            .fixtures_attempted
                        }
                      />

                      <Row
                        label="Should Goals erfolgreich"
                        value={
                          leagueReferenceResult.aggregate
                            .should_goals_comparison.fixtures_compared
                        }
                      />

                      <Row
                        label="Keine Statistiken"
                        value={
                          leagueReferenceResult.aggregate
                            .should_goals_comparison.diagnostics.no_statistics
                        }
                      />

                      <Row
                        label="Berechnungsfehler"
                        value={
                          leagueReferenceResult.aggregate
                            .should_goals_comparison.diagnostics
                            .calculation_errors
                        }
                      />

                      <Row
                        label="Ungültige Should Goals"
                        value={
                          leagueReferenceResult.aggregate
                            .should_goals_comparison.diagnostics
                            .invalid_should_goals
                        }
                      />
                    </Box>
                  </Paper>
                )}
              </>
            )}
        </Stack>
      </DialogContent>

      <ActionConfirmationDialog
        open={Boolean(actionConfirmation)}
        title={actionConfirmation?.title || ""}
        paragraphs={actionConfirmation?.paragraphs || []}
        confirmLabel={actionConfirmation?.confirmLabel || "Start"}
        confirmColor={actionConfirmation?.confirmColor || "primary"}
        onCancel={closeActionConfirmation}
        onConfirm={confirmAction}
      />

      <Dialog
        open={fixtureOddsConfirmOpen}
        onClose={() => setFixtureOddsConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Fixture Odds nachrüsten</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              {leagueId === ALL_LEAGUES
                ? "Alle Ligawettbewerbe werden " + "nacheinander geprüft."
                : `Die Liga ${selectedLeague?.name || leagueId} wird geprüft.`}
            </Typography>

            <Typography>
              Es werden ausschließlich bereits beendete Spiele aus der
              Vergangenheit berücksichtigt. Zukunftsspiele werden nicht
              berechnet oder verändert.
            </Typography>

            <Typography>
              Vorhandene Fixture-Odds-Datensätze werden ergänzt. Fehlende
              Datensätze werden mit den vorhandenen Team Settings und Season
              Parameters nachberechnet.
            </Typography>

            <Typography>
              Neu nachberechnete historische Spiele werden als
              Post-Match-Berechnung gekennzeichnet. Bereits vorhandene alte
              Berechnungen gelten zunächst als Pre-Match-Berechnungen.
            </Typography>

            <Typography>
              Für jedes Spiel wird versucht, die Sportmonks-Statistiken
              abzurufen und Should Goals zu speichern. Abhängig von der Anzahl
              der Ligen und Saisons kann der Vorgang längere Zeit dauern.
            </Typography>

            <Typography>
              Fehlende Statistiken verhindern nicht das Anlegen der Fixture
              Odds. In diesem Fall fehlen lediglich die Should Goals.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={hideFixtureOddsConfirmation}
                  onChange={(event) => {
                    const checked = event.target.checked;

                    setHideFixtureOddsConfirmation(checked);

                    localStorage.setItem(
                      "hideFixtureOddsConfirmation",
                      String(checked),
                    );
                  }}
                />
              }
              label="Diesen Hinweis künftig nicht mehr anzeigen"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFixtureOddsConfirmOpen(false)}>
            Cancel
          </Button>

          <Button variant="contained" onClick={startFixtureOddsBackfill}>
            Nachrüstung starten
          </Button>
        </DialogActions>
      </Dialog>

      <DialogActions>
        {activeSection === "league_reference" && (
          <Button
            variant="contained"
            disabled={running || !selectedLeagues.length}
            onClick={recalculateLeagueReferenceStats}
          >
            Recalculate
          </Button>
        )}

        <Button disabled={running} onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
